import { getAuth } from 'firebase/auth';
import { getApps } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  collection,
  collectionGroup,
  doc,
  documentId,
  endAt,
  getDoc,
  getDocs,
  orderBy,
  query,
  startAt,
  where,
  type DocumentData,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { db } from '../lib/firestore';
import { app } from '../lib/firebase';
import {
  canonicalReportingMonth,
  getAlternateReportingMonth,
  getFiscalQuarterMonths,
  parseReportingMonth,
  type ReportingMonth,
} from '../lib/reportingMonth';
import { type RoleContext } from '../lib/roles';
import { roleIndicatesSupervisorNurse } from '../lib/roles';
import { type RoleScope } from './auth/roleResolver';
import { DEFAULT_COMPLIANCE_CATEGORIES } from '../lib/complianceCategoryTypes';
import { resolveCanonicalCategoryId, submissionCategoryDocIdsToTry } from '../lib/canonicalIds';
import { lofLabelForUi } from '../lib/lofs';
import { NURSE_NOTES_TEAM_LABEL } from '../lib/complianceCategoryTypes';
import { departmentIdScopeKey, getFirestorePathDepartmentId, normalizeDepartmentId } from '../utils/normalizeIds';
import { deriveSubmissionMetrics, METRICS_KEY_MONTHLY_PCT } from '../lib/submissionMetrics';
import { hasTeamCategoryHospitalWideAccess } from '../lib/reportingTeamCategoryScope';
import { filterReportingSubmissionsByClientDepartments } from '../lib/reportingDepartmentClientFilter';
import { filterReportingDocsPreferringUsableContent } from '../lib/submissionReportingPick';

/** Inlined to avoid minifier TDZ when bundled (toFiniteNumber was minified to 'me'). */
function toFiniteNum(val: unknown): number | null {
  if (typeof val === 'number' && Number.isFinite(val)) return val;
  if (typeof val === 'string') {
    const t = val.trim();
    if (t && Number.isFinite(Number(t))) return Number(t);
  }
  return null;
}

// Role IDs that must not be used as department IDs (canonical + legacy)
const INVALID_DEPARTMENT_TOKENS = new Set([
  'SN', 'HWN',
  'SUPERVISOR_NURSE', 'HEAD_WARD_NURSE',
]);

const isInvalidDepartmentId = (value: unknown): boolean => {
  const normalized = normalizeDepartmentId(value);
  if (!normalized) return true;
  if (INVALID_DEPARTMENT_TOKENS.has(normalized)) return true;
  return false;
};

export interface ReportingSubmission extends DocumentData {
  id: string;
  category?: string;
  categoryId?: string;
  categoryTitle?: string;
  departmentId?: string;
  reportingMonth?: ReportingMonth;
  departmentName?: string;
  metrics?: Record<string, number>;
  metricsVersion?: number;
  status?: string;
  submittedAt?: string | null;
  submittedBy?: string | null;
  submittedByName?: string | null;
  updatedAt?: string | null;
  userId?: string;
  userDisplayName?: string;
  isCrossAudit?: boolean;
  crossAudit?: boolean;
  sourceCategoryId?: string;
  submissionId?: string; // For deduplication - consistent ID across storage locations
  auditedDepartmentId?: string; // For cross-audit deduplication
}

export interface ReportingAggregateResult {
  submissions: ReportingSubmission[];
  totals: Record<string, number>;
  averages: Record<string, number>;
  months: ReportingMonth[];
  message?: string;
  submissionsCount?: number;
}

export interface ReportingResultSummary {
  totals: Record<string, number>;
  averages: Record<string, number>;
  submissionsCount: number;
  meta?: Record<string, unknown>;
}

export interface ReportingScopeOverrides {
  departmentIds?: string[] | null;
  lofIds?: string[] | null;
}

export interface ReportingFetchOptions {
  mode?: 'default' | 'team';
  teamAllowedCategoryIds?: string[];
  /** Year dashboards use `summary` to avoid retaining full submission bodies in memory. */
  fetchMode?: 'summary' | 'detail';
}

/** Slim per-month payload for year charts (no narrative / audit blobs). */
export interface ReportingMonthSummary {
  reportingMonth: ReportingMonth;
  totals: Record<string, number>;
  averages: Record<string, number>;
  submissionsCount: number;
  message?: string;
}

export type ReportingYearMonthEntry = {
  reportingMonth: ReportingMonth;
  result: ReportingAggregateResult;
  error?: string;
  status: 'ok' | 'error';
};

interface DepartmentScopeRecord {
  id: string;
  name?: string;
  lofId?: string;
}

const isReportingDebugEnabled = (): boolean => {
  try {
    return typeof window !== 'undefined' && window.localStorage.getItem('nidata_debug_reporting') === '1';
  } catch {
    return false;
  }
};

const resolveCategoryId = (category?: string): string | undefined => {
  if (!category) return undefined;

  const normalized = category.trim();
  if (!normalized) return undefined;

  const match = DEFAULT_COMPLIANCE_CATEGORIES.find(
    (defaultCategory) => defaultCategory.id === normalized || defaultCategory.title === normalized,
  );

  return match?.id ?? normalized;
};

const resolveCategoryTitle = (categoryId?: string, categoryTitle?: string, fallback?: string): string => {
  const explicit = categoryTitle?.trim();
  if (explicit) return explicit;

  const normalizedId = categoryId?.trim();
  if (normalizedId) {
    const match = DEFAULT_COMPLIANCE_CATEGORIES.find(
      (defaultCategory) => defaultCategory.id === normalizedId || defaultCategory.title === normalizedId,
    );
    return match?.title ?? normalizedId;
  }

  return fallback?.trim() || 'unknown';
};

const extractRefId = (value: unknown): string | undefined => {
  if (!value) return undefined;
  if (typeof value === 'string') return value.trim() || undefined;

  if (typeof value === 'object' && value !== null) {
    const candidate = value as { id?: unknown; path?: unknown };
    if (typeof candidate.id === 'string' && candidate.id.trim()) {
      return candidate.id.trim();
    }
    if (typeof candidate.path === 'string') {
      const fromPath = candidate.path.split('/').pop();
      return fromPath?.trim() || undefined;
    }
  }

  return undefined;
};

const extractDepartmentIdsFromLofDoc = (data: Record<string, unknown> | null | undefined): string[] => {
  if (!data) return [];
  const candidates = [
    data.departmentRefs,
    data.department,
    data.departments,
    data.departmentIds,
  ];

  const ids = new Set<string>();
  candidates.forEach((entry) => {
    if (!Array.isArray(entry)) return;
    entry.forEach((item) => {
      const extracted = extractRefId(item);
      if (extracted) ids.add(extracted);
    });
  });

  return Array.from(ids);
};

const fetchDepartmentIdsByLofIds = async (lofIds: string[]): Promise<string[]> => {
  const ids = (lofIds ?? []).filter((id) => typeof id === 'string' && id.trim());
  if (!ids.length) return [];

  const allDeptIds = new Set<string>();
  const departmentsCol = collection(db, 'departments');

  for (const lofId of ids) {
    const lofRef = doc(db, 'lofs', lofId);
    const fromLofDoc = getDoc(lofRef).then((snap) =>
      snap.exists() ? extractDepartmentIdsFromLofDoc(snap.data() as Record<string, unknown>) : [],
    );
    const byLofId = getDocs(query(departmentsCol, where('lofId', '==', lofId))).then((snap) =>
      snap.docs.map((d) => d.id),
    );
    const byLofLegacy = getDocs(query(departmentsCol, where('lof', '==', lofId))).then((snap) =>
      snap.docs.map((d) => d.id),
    );
    const byLofRef = getDocs(query(departmentsCol, where('lofRef', '==', lofRef))).then((snap) =>
      snap.docs.map((d) => d.id),
    );
    const byLOF = getDocs(query(departmentsCol, where('LOF', '==', lofRef))).then((snap) =>
      snap.docs.map((d) => d.id),
    );
    const [fromLof, fromLofId, fromLofLegacy, fromLofRef, fromLOF] = await Promise.all([
      fromLofDoc,
      byLofId,
      byLofLegacy,
      byLofRef,
      byLOF,
    ]);
    [...fromLof, ...fromLofId, ...fromLofLegacy, ...fromLofRef, ...fromLOF].forEach((id) => allDeptIds.add(id));
  }
  return Array.from(allDeptIds);
};

/** Case variants for matching departments (lofId may be "ICU" vs "icu" in lofs collection). */
const getLofIdCaseVariants = (id: string): string[] => {
  const t = id.trim();
  if (!t) return [];
  const lower = t.toLowerCase();
  const upper = t.toUpperCase();
  return Array.from(new Set([t, lower, upper]));
};

const fetchSupervisorDepartmentIdsFromLof = async (roleContext?: RoleContext | null): Promise<string[]> => {
  const lofIds = (roleContext?.lofIds ?? []).filter((id) => typeof id === 'string' && id.trim());
  const singleLofId = roleContext?.lofId?.trim();
  const idsToQuery =
    lofIds.length > 0
      ? Array.from(new Set([...lofIds, ...(singleLofId && !lofIds.includes(singleLofId) ? [singleLofId] : [])]))
      : singleLofId
        ? [singleLofId]
        : [];
  if (!idsToQuery.length) return [];

  const allDeptIds = new Set<string>();
  const departmentsCol = collection(db, 'departments');

  for (const lofId of idsToQuery) {
    const fromLofDoc = (async () => {
      const lofDocRef = doc(db, 'lofs', lofId);
      const lofSnap = await getDoc(lofDocRef);
      return lofSnap.exists() ? extractDepartmentIdsFromLofDoc(lofSnap.data() as Record<string, unknown>) : [];
    })();
    const fromLof = await fromLofDoc;
    fromLof.forEach((id) => allDeptIds.add(id));

    const variants = getLofIdCaseVariants(lofId);
    const lofRefForDept = doc(db, 'lofs', lofId);
    const queries: Promise<string[]>[] = [
      getDocs(query(departmentsCol, where('lofRef', '==', lofRefForDept))).then((snap) => snap.docs.map((d) => d.id)),
      getDocs(query(departmentsCol, where('LOF', '==', lofRefForDept))).then((snap) => snap.docs.map((d) => d.id)),
    ];
    for (const v of variants) {
      queries.push(
        getDocs(query(departmentsCol, where('lofId', '==', v))).then((snap) => snap.docs.map((d) => d.id)),
      );
      queries.push(
        getDocs(query(departmentsCol, where('lof', '==', v))).then((snap) => snap.docs.map((d) => d.id)),
      );
    }
    const results = await Promise.all(queries);
    results.flat().forEach((id) => allDeptIds.add(id));

    // Fallback for mixed-case LOF refs/ids in legacy data:
    // if targeted queries find nothing, scan departments once and match case-insensitively.
    if (allDeptIds.size === 0) {
      const allDepartmentsSnapshot = await getDocs(departmentsCol);
      const normalizedVariants = new Set(getLofIdCaseVariants(lofId).map((v) => v.toLowerCase()));
      allDepartmentsSnapshot.docs.forEach((departmentDoc) => {
        const deptData = departmentDoc.data() as Record<string, unknown>;
        const lofIdField =
          typeof deptData.lofId === 'string'
            ? deptData.lofId.trim().toLowerCase()
            : typeof deptData.lof === 'string'
              ? deptData.lof.trim().toLowerCase()
              : '';
        const lofRefField = extractRefId(deptData.lofRef ?? deptData.LOF)?.toLowerCase() ?? '';
        if (normalizedVariants.has(lofIdField) || normalizedVariants.has(lofRefField)) {
          allDeptIds.add(departmentDoc.id);
        }
      });
    }
  }

  return Array.from(allDeptIds);
};

const formatReportingMonth = (year: number, monthIndex: number): ReportingMonth => {
  const normalized = new Date(year, monthIndex, 1);
  const month = String(normalized.getMonth() + 1).padStart(2, '0');
  return `${normalized.getFullYear()}-${month}` as ReportingMonth;
};

const resolveDepartmentFilters = (
  roleScope?: RoleScope | null,
  overrides?: ReportingScopeOverrides,
): string[] | undefined => {
  if (overrides && 'departmentIds' in overrides) {
    const overrideList = Array.isArray(overrides.departmentIds) ? overrides.departmentIds : null;
    const overrideDepartments = (overrideList ?? []).filter(Boolean) as string[];
    if (overrideDepartments.includes('*')) return undefined;
    if (overrideDepartments.length) return overrideDepartments;
  }

  const scopeDepartments = roleScope?.view?.departmentIds ?? roleScope?.departmentIds ?? [];

  if (scopeDepartments.includes('*')) return undefined;

  const departmentIds = scopeDepartments.filter(Boolean) as string[];
  return departmentIds.length ? departmentIds : [];
};

const resolveLofOverrides = (overrides?: ReportingScopeOverrides): string[] | undefined => {
  if (!overrides || !('lofIds' in overrides)) return undefined;
  const lofIds = (overrides.lofIds ?? []).filter(Boolean) as string[];
  return lofIds.length ? lofIds : undefined;
};

export const computeAggregates = (submissions: ReportingSubmission[]) => {
  const totals: Record<string, number> = {};

  submissions.forEach((submission) => {
    let metrics = submission.metrics ?? {};
    if (Object.keys(metrics).length === 0 && (submission as Record<string, unknown>).answers) {
      metrics = deriveSubmissionMetrics(submission);
    }
    Object.entries(metrics).forEach(([key, value]) => {
      const n = toFiniteNum(value);
      if (n === null) return;
      totals[key] = (totals[key] ?? 0) + n;
    });
  });

  const averages: Record<string, number> = {};
  const count = submissions.length || 1;

  Object.entries(totals).forEach(([key, value]) => {
    if (key.endsWith('_pct')) {
      const base = key.replace(/_pct$/, '');
      const scoreKey = `${base}_score`;
      const totalKey = `${base}_total`;
      const sumScore = totals[scoreKey] ?? 0;
      const sumTotal = totals[totalKey] ?? 0;
      if (sumTotal > 0) {
        averages[key] = Math.round((sumScore / sumTotal) * 10000) / 100;
        return;
      }
    }
    averages[key] = value / count;
  });

  return { totals, averages };
};

/** Attach derived metrics when Firestore docs only store answers/charts (reporting charts need this). */
export const enrichReportingSubmissionWithMetrics = (
  submission: ReportingSubmission,
): ReportingSubmission => {
  if (submission.metrics && Object.keys(submission.metrics).length > 0) return submission;
  const derived = deriveSubmissionMetrics(submission);
  if (Object.keys(derived).length === 0) return submission;
  return { ...submission, metrics: derived };
};

/** Run async work with limited parallelism; preserves output order (index-aligned). */
async function mapWithPool<T, R>(
  items: readonly T[],
  poolSize: number,
  fn: (item: T, index: number) => Promise<R>,
): Promise<R[]> {
  const n = items.length;
  if (n === 0) return [];
  const results = new Array<R>(n);
  let next = 0;
  const workers = Math.max(1, Math.min(poolSize, n));
  const worker = async (): Promise<void> => {
    for (;;) {
      const i = next++;
      if (i >= n) break;
      results[i] = await fn(items[i]!, i);
    }
  };
  await Promise.all(Array.from({ length: workers }, () => worker()));
  return results;
}

/** Non-NN yearly month fetches — avoids 12 unconstrained parallel pipelines. */
export const DEFAULT_YEARLY_POOL_SIZE = 6;

/** NN hospital-wide yearly: full parallel (12 callables) stalled; fully sequential took ~346s in prod logs. */
const NN_YEARLY_HOSPITAL_WIDE_POOL = 3;

/** Wildcard hospital-wide dept path reads per month. */
const DEPARTMENT_PATH_DOCS_POOL_SIZE = 12;

export const applySummaryFetchMode = (
  result: ReportingAggregateResult,
  fetchMode?: ReportingFetchOptions['fetchMode'],
): ReportingAggregateResult => {
  if (fetchMode !== 'summary') return result;
  const count = result.submissions.length;
  return {
    totals: result.totals,
    averages: result.averages,
    months: result.months,
    message: result.message,
    submissions: [],
    submissionsCount: count,
  };
};

/** Count submissions when year/summary mode strips the `submissions` array. */
export const getReportingSubmissionCount = (
  result: ReportingAggregateResult | null | undefined,
): number => {
  if (!result) return 0;
  if (typeof result.submissionsCount === 'number' && Number.isFinite(result.submissionsCount)) {
    return result.submissionsCount;
  }
  return result.submissions?.length ?? 0;
};

/** True when an aggregate has ward/month data (summary or detail shape). */
export const hasReportingAggregateData = (
  result: ReportingAggregateResult | null | undefined,
): boolean => {
  if (!result) return false;
  if (getReportingSubmissionCount(result) > 0) return true;
  const total = result.totals?.monthly_total;
  return typeof total === 'number' && Number.isFinite(total) && total > 0;
};

export type ReportingMonthsDetailProgressCallback = (
  reportingMonth: ReportingMonth,
  loadedCount: number,
  totalCount: number,
) => void;

/** Load full submission bodies for months that only have summary aggregates (year/trimester NN path). */
export const fetchReportingMonthsDetail = async (
  months: ReportingMonth[],
  params: GetReportingResultParams,
  onProgress?: ReportingMonthsDetailProgressCallback,
): Promise<ReportingSubmission[]> => {
  if (!months.length) return [];
  const detailOptions: ReportingFetchOptions = {
    ...(params.options ?? {}),
    fetchMode: 'detail',
  };
  const serialize = shouldSerializeNnHospitalWideYearlyFetch(
    params.category,
    params.roleContext,
    detailOptions,
  );
  const poolSize = serialize ? NN_YEARLY_HOSPITAL_WIDE_POOL : DEFAULT_YEARLY_POOL_SIZE;
  const totalCount = months.length;
  let loadedCount = 0;
  const results = await mapWithPool(months, poolSize, async (month) => {
    const cached = readReportingSubmissionsBodyCache(
      month,
      params.category,
      params.overrides,
      detailOptions,
    );
    if (cached) {
      loadedCount += 1;
      onProgress?.(month, loadedCount, totalCount);
      return {
        submissions: cached,
        totals: {},
        averages: {},
        months: [month],
      };
    }
    const result = await fetchMonthlyReportingData(
      month,
      params.category,
      params.roleScope,
      params.roleContext,
      params.overrides,
      detailOptions,
    );
    loadedCount += 1;
    onProgress?.(month, loadedCount, totalCount);
    return result;
  });
  return results.flatMap((result, index) =>
    result.submissions.map((submission) =>
      enrichReportingSubmissionWithMetrics({
        ...submission,
        reportingMonth: (submission.reportingMonth ?? months[index]!) as ReportingMonth,
      }),
    ),
  );
};

export const toReportingMonthSummary = (
  reportingMonth: ReportingMonth,
  result: ReportingAggregateResult,
): ReportingMonthSummary => ({
  reportingMonth,
  totals: result.totals,
  averages: result.averages,
  submissionsCount: result.submissionsCount ?? result.submissions.length,
  message: result.message,
});

/**
 * Pooled nursing completeness as a true percentage (0–100) for a reporting aggregate.
 * Prefer `averages.monthly_pct` from {@link computeAggregates} (ratio of summed scores to summed totals).
 */
export const pooledMonthlyCompletenessPct = (result: ReportingAggregateResult | null | undefined): number | null => {
  if (!result) return null;
  const fromAvg = result.averages[METRICS_KEY_MONTHLY_PCT];
  if (typeof fromAvg === 'number' && Number.isFinite(fromAvg)) return fromAvg;
  const total = result.totals['monthly_total'];
  const score = result.totals['monthly_score'];
  if (typeof total === 'number' && typeof score === 'number' && total > 0 && Number.isFinite(total) && Number.isFinite(score)) {
    return Math.round((score / total) * 10000) / 100;
  }
  return null;
};

export interface GetReportingResultParams {
  reportingMonth: ReportingMonth;
  category?: string;
  roleScope?: RoleScope | null;
  roleContext?: RoleContext | null;
  overrides?: ReportingScopeOverrides;
  options?: ReportingFetchOptions;
}

const normalizeDate = (value: unknown) => {
  if (!value) return null;
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'string') return value;
  if (typeof value === 'object' && value !== null && 'toDate' in value && typeof (value as any).toDate === 'function') {
    try {
      return (value as { toDate: () => Date }).toDate().toISOString();
    } catch {
      return null;
    }
  }
  return null;
};

const normalizeRoleValue = (value: unknown): string | null => {
  if (!value) return null;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized ? normalized : null;
  }
  if (typeof value === 'object' && 'role' in value) {
    const roleValue = (value as { role?: unknown }).role;
    if (typeof roleValue === 'string') {
      const normalized = roleValue.trim().toLowerCase();
      return normalized ? normalized : null;
    }
  }
  return null;
};

const roleIndicatesHeadWardNurse = (role: string | null | undefined): boolean => {
  if (!role) return false;
  const normalized = role.trim().toLowerCase().replace(/[-\s]+/g, '_');
  return normalized === 'hwn' || normalized === 'head_ward_nurse' || normalized.includes('head_ward');
};

const hasHeadWardScope = (roleContext?: RoleContext | null): boolean => {
  if (!roleContext) return false;
  const roles: string[] = [];
  const pushRole = (role?: unknown) => {
    const normalized = normalizeRoleValue(role);
    if (normalized) roles.push(normalized);
  };

  pushRole(roleContext.generalRole);
  pushRole(roleContext.positionRole);
  pushRole(roleContext.departmentRole);
  (roleContext.roles ?? []).forEach((role) => pushRole(role));
  (roleContext.departmentRoles ?? []).forEach((role) => pushRole(role));

  return roles.some(roleIndicatesHeadWardNurse);
};

const hasSupervisorScope = (roleContext?: RoleContext | null): boolean => {
  if (!roleContext) return false;
  if (hasHeadWardScope(roleContext)) return false;
  const roles: string[] = [];
  const pushRole = (role?: unknown) => {
    const normalized = normalizeRoleValue(role);
    if (normalized) roles.push(normalized);
  };

  pushRole(roleContext.generalRole);
  pushRole(roleContext.positionRole);
  pushRole(roleContext.departmentRole);
  (roleContext.roles ?? []).forEach((role) => pushRole(role));
  (roleContext.departmentRoles ?? []).forEach((role) => pushRole(role));
  pushRole(roleContext.teamRole);
  (roleContext.teamRoles ?? []).forEach((role) => pushRole(role));

  return roles.some(roleIndicatesSupervisorNurse);
};

const normalizeDepartmentRecord = (snapshot: { id: string; data: () => DocumentData }): DepartmentScopeRecord => {
  const data = snapshot.data() ?? {};
  const lofId =
    (typeof data.lofId === 'string' && data.lofId.trim()) ||
    (typeof data.lof === 'string' && data.lof.trim()) ||
    (typeof data.LOF === 'string' && data.LOF.trim()) ||
    undefined;

  return {
    id: snapshot.id,
    name: (data.name as string | undefined) ?? snapshot.id,
    lofId,
  };
};

let _departmentCache: { data: DepartmentScopeRecord[]; expiry: number } | null = null;

/** Clears department cache. Use in tests to avoid stale cache between tests. */
export const __testingClearDepartmentCache = (): void => {
  _departmentCache = null;
};

/** Dedupe concurrent identical monthly reporting requests (e.g. year aggregate + yearly breakdown). */
const inflightMonthlyReporting = new Map<string, Promise<ReportingAggregateResult>>();

/** Full submission bodies keyed without fetchMode — year summary strips bodies but detail charts reuse this cache. */
const reportingSubmissionsBodyCache = new Map<string, ReportingSubmission[]>();

function reportingSubmissionsBodyCacheKey(
  reportingMonth: ReportingMonth,
  category: string | undefined,
  overrides: ReportingScopeOverrides | undefined,
  options: ReportingFetchOptions | undefined,
): string {
  const uid = getAuth(app).currentUser?.uid ?? '';
  const mode = options?.mode ?? 'default';
  const deptPart = JSON.stringify(overrides?.departmentIds ?? null);
  const lofPart = JSON.stringify(overrides?.lofIds ?? null);
  return `${uid}\u001f${mode}\u001f${reportingMonth}\u001f${category ?? ''}\u001f${deptPart}\u001f${lofPart}`;
}

function rememberReportingSubmissionsBody(
  reportingMonth: ReportingMonth,
  category: string | undefined,
  overrides: ReportingScopeOverrides | undefined,
  options: ReportingFetchOptions | undefined,
  submissions: ReportingSubmission[],
): void {
  if (!submissions.length) return;
  reportingSubmissionsBodyCache.set(
    reportingSubmissionsBodyCacheKey(reportingMonth, category, overrides, options),
    submissions,
  );
}

function readReportingSubmissionsBodyCache(
  reportingMonth: ReportingMonth,
  category: string | undefined,
  overrides: ReportingScopeOverrides | undefined,
  options: ReportingFetchOptions | undefined,
): ReportingSubmission[] | undefined {
  return reportingSubmissionsBodyCache.get(
    reportingSubmissionsBodyCacheKey(reportingMonth, category, overrides, options),
  );
}

/** Clears in-flight monthly reporting dedupe map (vitest). */
export const __testingClearInflightMonthlyReporting = (): void => {
  inflightMonthlyReporting.clear();
  reportingSubmissionsBodyCache.clear();
};

/** Dedupe concurrent identical yearly pipelines (Dashboard + ReportingTable often mount together). */
const inflightYearlyReporting = new Map<
  string,
  Promise<Array<{ reportingMonth: ReportingMonth; result: ReportingAggregateResult }>>
>();

/** Clears in-flight yearly reporting dedupe map (vitest). */
export const __testingClearInflightYearlyReporting = (): void => {
  inflightYearlyReporting.clear();
};

/** Clears client reporting caches on sign-out (paired with queryClient.clear()). */
export const clearReportingClientCaches = (): void => {
  inflightMonthlyReporting.clear();
  inflightYearlyReporting.clear();
  reportingSubmissionsBodyCache.clear();
  nnNurseNotesRawHits.clear();
  nnNurseNotesRawInflight.clear();
  _departmentCache = null;
};

export const buildReportingYearQueryKey = (params: GetReportingResultParams): readonly [
  'reporting-year',
  string,
] => ['reporting-year', yearlyReportingInflightKey(params)] as const;

function yearlyReportingInflightKey(params: GetReportingResultParams): string {
  const uid = getAuth(app).currentUser?.uid ?? '';
  const mode = params.options?.mode ?? 'default';
  const deptPart = JSON.stringify(params.overrides?.departmentIds ?? null);
  const lofPart = JSON.stringify(params.overrides?.lofIds ?? null);
  const tac = JSON.stringify(params.options?.teamAllowedCategoryIds ?? null);
  const rs = params.roleScope;
  const rsKey = JSON.stringify(
    rs
      ? {
          vd: [...(rs.view?.departmentIds ?? [])].map(String).sort(),
          vt: [...(rs.view?.teamIds ?? [])].map(String).sort(),
          dd: [...(rs.departmentIds ?? [])].map(String).sort(),
        }
      : null,
  );
  const rc = params.roleContext;
  const rcKey = !rc
    ? null
    : JSON.stringify({
        gr: rc.generalRole ?? '',
        pr: rc.positionRole ?? '',
        ri: rc.roleId ?? '',
        roles: [...(rc.roles ?? [])].map(String).sort(),
        tr: [...(rc.teamRoles ?? [])].map(String).sort(),
        ti: [...(rc.teamIds ?? [])].map(String).sort(),
        tt: rc.teamRole ?? '',
        pt: rc.primaryTeamId ?? '',
        pc: rc.primaryTeamCode ?? '',
        tac: [...(rc.teamAllowedCategoryIds ?? [])].map(String).sort(),
        lof: rc.lofId ?? '',
        lofs: [...(rc.lofIds ?? [])].map(String).sort(),
        low: [...(rc.lineOfWorkDepartmentIds ?? [])].map(String).sort(),
      });
  return `${uid}\u001f${mode}\u001f${params.reportingMonth}\u001f${params.category ?? ''}\u001f${deptPart}\u001f${lofPart}\u001f${tac}\u001f${rsKey}\u001f${rcKey}`;
}

/** Full NN nurse_notes cloud payload per month (not keyed by dept filter — sticky filter only re-slices client-side). */
type NnNurseNotesRawPayload = {
  submissions: ReportingSubmission[];
  months: ReportingMonth[];
  message?: string;
};

const nnNurseNotesRawHits = new Map<string, NnNurseNotesRawPayload>();
const nnNurseNotesRawInflight = new Map<string, Promise<NnNurseNotesRawPayload | null>>();

function nnNurseNotesRawCacheKey(reportingMonth: ReportingMonth): string {
  return `${getAuth(app).currentUser?.uid ?? ''}\u001f${reportingMonth}\u001fnurse_notes`;
}

async function getNnNurseNotesRawPayload(reportingMonth: ReportingMonth): Promise<NnNurseNotesRawPayload | null> {
  const key = nnNurseNotesRawCacheKey(reportingMonth);
  const hit = nnNurseNotesRawHits.get(key);
  if (hit) return hit;

  let inflight = nnNurseNotesRawInflight.get(key);
  if (!inflight) {
    inflight = (async (): Promise<NnNurseNotesRawPayload | null> => {
      const nnCloud = await fetchTeamReportingDataCloud(reportingMonth, 'nurse_notes');
      if (!nnCloud) return null;
      const payload: NnNurseNotesRawPayload = {
        submissions: nnCloud.submissions ?? [],
        months: (nnCloud.months ?? [reportingMonth]) as ReportingMonth[],
        message: nnCloud.message,
      };
      nnNurseNotesRawHits.set(key, payload);
      return payload;
    })().finally(() => {
      nnNurseNotesRawInflight.delete(key);
    });
    nnNurseNotesRawInflight.set(key, inflight);
  }

  return inflight;
}

/** Clears NN nurse_notes raw month cache (vitest / forced refresh). */
export const __testingClearNnNurseNotesRawCache = (): void => {
  nnNurseNotesRawHits.clear();
  nnNurseNotesRawInflight.clear();
};

const fetchDepartments = async (): Promise<DepartmentScopeRecord[]> => {
  if (_departmentCache && Date.now() < _departmentCache.expiry) {
    return _departmentCache.data;
  }
  const snapshot = await getDocs(collection(db, 'departments'));
  const records = snapshot.docs.map((docSnap) =>
    normalizeDepartmentRecord({ id: docSnap.id, data: () => docSnap.data() }),
  );
  _departmentCache = { data: records, expiry: Date.now() + 5 * 60 * 1000 };
  return records;
};

const normalizeDepartmentSubmissionDoc = (
  snapshot: { id: string; data: () => DocumentData },
  reportingMonth: ReportingMonth,
): ReportingSubmission => {
  const data = snapshot.data() ?? {};
  const legacyCategory =
    (typeof data.category === 'string' && data.category.trim()) ||
    (typeof data.categoryTitle === 'string' && data.categoryTitle.trim()) ||
    snapshot.id;
  // New model: HWN of audited dept inputs cross-audit data; doc ID CROSS_AUDIT_NURSING__* identifies it
  const isCrossAudit =
    data.crossAudit === true ||
    data.type === 'cross_audit' ||
    (typeof snapshot.id === 'string' && snapshot.id.startsWith('CROSS_AUDIT_NURSING'));
  const sourceCategoryId = typeof data.sourceCategoryId === 'string' ? data.sourceCategoryId.trim() : undefined;
  const sourceCategoryTitle =
    typeof data.sourceCategoryTitle === 'string' ? data.sourceCategoryTitle.trim() : undefined;
  const auditedDepartmentId =
    typeof data.auditedDepartmentId === 'string' ? data.auditedDepartmentId.trim() : undefined;
  const categoryId =
    resolveCategoryId(
      (isCrossAudit && sourceCategoryId ? sourceCategoryId : undefined) ??
        (data.categoryId as string | undefined) ??
        (data.category as string | undefined) ??
        legacyCategory,
    ) ?? snapshot.id;
  const categoryTitle = resolveCategoryTitle(
    categoryId,
    (isCrossAudit && sourceCategoryTitle ? sourceCategoryTitle : undefined) ??
      (data.categoryTitle as string | undefined) ??
      (data.category as string | undefined),
    snapshot.id,
  );

  // Extract submissionId before spread to ensure it's available
  const submissionIdValue = (data.submissionId as string | undefined) ?? ((data as any).canonicalSubmissionId as string | undefined);
  
  // Ensure submissionId is always set correctly (defensive check)
  const finalSubmissionId = submissionIdValue && typeof submissionIdValue === 'string' && submissionIdValue.trim() 
    ? submissionIdValue.trim() 
    : undefined;
  
  const normalized = {
    id: finalSubmissionId ?? snapshot.id,
    ...data,
    // Preserve submissionId for deduplication (explicit assignment after spread ensures it's set)
    // Use finalSubmissionId to ensure it's always a trimmed string or undefined
    submissionId: finalSubmissionId,
    // Preserve auditedDepartmentId for cross-audit deduplication
    auditedDepartmentId: isCrossAudit ? auditedDepartmentId : undefined,
    category: legacyCategory,
    categoryTitle,
    categoryId,
    reportingMonth: canonicalReportingMonth(data.reportingMonth, reportingMonth),
    status: (data.status as string | undefined) ?? 'submitted',
    isCrossAudit,
    submittedAt: normalizeDate(data.submittedAt),
    updatedAt: normalizeDate(data.updatedAt),
    departmentId: (isCrossAudit ? auditedDepartmentId : undefined) ?? (data.departmentId as string | undefined),
    departmentName: data.departmentName as string | undefined,
    submittedByName: data.submittedByName as string | undefined,
    submittedBy: data.submittedBy as string | undefined,
    userId: (data.submittedBy as string | undefined) ?? (data.userId as string | undefined),
    userDisplayName: (data.submittedByName as string | undefined) ?? (data.userDisplayName as string | undefined),
  };
  return normalized;
};

const normalizeLofSubmissionDoc = (
  snapshot: { id: string; data: () => DocumentData },
  reportingMonth: ReportingMonth,
  lofId?: string,
): ReportingSubmission => {
  const data = snapshot.data() ?? {};
  const metadata = data.metadata && typeof data.metadata === 'object' && !Array.isArray(data.metadata) ? data.metadata : {};
  const resolvedLofId = (data.lofId as string | undefined) ?? (lofId ?? undefined);
  const metadataCategory =
    (metadata as { category?: string; categoryTitle?: string; categoryId?: string }).categoryTitle ??
    (metadata as { category?: string; categoryTitle?: string; categoryId?: string }).category;
  const legacyCategory =
    (typeof data.category === 'string' && data.category.trim()) ||
    (typeof data.categoryTitle === 'string' && data.categoryTitle.trim()) ||
    (typeof metadataCategory === 'string' && metadataCategory.trim()) ||
    snapshot.id;
  const categoryId =
    resolveCategoryId(
      (data.categoryId as string | undefined) ??
        (metadata as { categoryId?: string }).categoryId ??
        (data.category as string | undefined) ??
        legacyCategory,
    ) ?? snapshot.id;
  const categoryTitle = resolveCategoryTitle(
    categoryId,
    (data.categoryTitle as string | undefined) ??
      (data.category as string | undefined) ??
      (metadataCategory as string | undefined),
    snapshot.id,
  );
  const submittedByName =
    (metadata as { submittedByName?: string }).submittedByName ??
    (data.submittedByName as string | undefined) ??
    undefined;
  const submittedBy =
    (metadata as { submittedBy?: string }).submittedBy ?? (data.submittedBy as string | undefined) ?? undefined;

  return {
    id: (data.submissionId as string | undefined) ?? snapshot.id,
    ...data,
    category: legacyCategory,
    categoryTitle,
    categoryId,
    reportingMonth: canonicalReportingMonth(data.reportingMonth, reportingMonth),
    status: (data.status as string | undefined) ?? (metadata as { status?: string }).status ?? 'submitted',
    submittedAt: normalizeDate(data.submittedAt ?? (metadata as { submittedAt?: unknown }).submittedAt),
    updatedAt: normalizeDate(data.updatedAt ?? (metadata as { updatedAt?: unknown }).updatedAt),
    departmentId: (data.departmentId as string | undefined) ?? resolvedLofId,
    departmentName:
      (data.departmentName as string | undefined) ??
      (resolvedLofId ? lofLabelForUi(resolvedLofId) : undefined),
    submittedByName,
    submittedBy,
    userId: (data.userId as string | undefined) ?? submittedBy,
    userDisplayName:
      (data.userDisplayName as string | undefined) ??
      submittedByName ??
      (metadata as { submittedByName?: string }).submittedByName,
  };
};

const formatReportingFetchError = (
  errorDetails: { code?: string; message?: string },
  firestorePath?: string,
) => {
  const errorCode = errorDetails?.code ?? 'unknown';
  const errorMessage = errorDetails?.message ?? 'Unknown Firestore error';
  const pathSuffix = firestorePath ? ` (path: ${firestorePath})` : '';
  return new Error(`Firestore error (${errorCode}) while loading reporting data${pathSuffix}: ${errorMessage}`);
};

export const fetchDepartmentPathDocs = async (
  reportingMonth: ReportingMonth,
  departmentIds: string[],
  category?: string,
  roleContext?: RoleContext | null,
  departmentRecords?: DepartmentScopeRecord[],
): Promise<ReportingSubmission[]> => {
  if (departmentIds.length === 0) {
    // Always log when empty - common cause of "0 submissions" for HWN
    console.warn('[Report trace] fetchDepartmentPathDocs: empty departmentIds, returning []', {
      reportingMonth,
      category: category ?? null,
    });
    return [];
  }

  if (import.meta.env.DEV || isReportingDebugEnabled()) {
    console.info('[Report trace] fetchDepartmentPathDocs called', {
      departmentIds,
      reportingMonth,
      category: category ?? null,
      departmentRecordsCount: (departmentRecords ?? []).length,
    });
  }

  // Map canonical IDs (e.g. LINE_OF_WORK_DEPARTMENT_MAP OPD_SxOrthoEENT) to actual Firestore doc IDs (OPD-SxOrthoEENT)
  const allDepartmentRecords = departmentRecords ?? [];
  const departmentRecordsByScopeKey = new Map<string, string>();
  allDepartmentRecords.forEach((dept) => {
    const key = departmentIdScopeKey(dept.id);
    if (!key || departmentRecordsByScopeKey.has(key)) return;
    departmentRecordsByScopeKey.set(key, dept.id);
  });
  const mappedDepartmentIds = departmentIds.map((queryId) => {
    if (isInvalidDepartmentId(queryId)) return null;
    const exactMatch = allDepartmentRecords.find((dept) => dept.id === queryId);
    if (exactMatch) return getFirestorePathDepartmentId(exactMatch.id);

    if (allDepartmentRecords.length === 0) {
      return getFirestorePathDepartmentId(queryId);
    }

    const scopeKey = departmentIdScopeKey(queryId);
    if (!scopeKey) return null;

    const resolvedId = departmentRecordsByScopeKey.get(scopeKey);
    if (resolvedId) return getFirestorePathDepartmentId(resolvedId);

    if (import.meta.env.DEV || isReportingDebugEnabled()) {
      console.warn('[Report scope] Skip unresolved department id', {
        queryId,
        reportingMonth,
        category: category ?? null,
      });
    }
    return null;
  }).filter((id): id is string => id != null);

  const uniqueMappedIds = Array.from(new Set(mappedDepartmentIds));

  if (isReportingDebugEnabled()) {
    console.info('Report fetch department path docs', {
      departmentIds,
      mappedDepartmentIds: uniqueMappedIds,
      reportingMonth,
      category: category ?? null,
    });
  }

  const seenErrors = new Set<string>();
  const submissionsByDepartment = await mapWithPool(
    uniqueMappedIds,
    DEPARTMENT_PATH_DOCS_POOL_SIZE,
    async (firestoreDepartmentId): Promise<PromiseSettledResult<ReportingSubmission[]>> => {
      try {
      if (isInvalidDepartmentId(firestoreDepartmentId)) {
        console.error('Skipping invalid departmentId for reporting fetch', {
          departmentId: firestoreDepartmentId,
          reportingMonth,
          category,
        });
        return { status: 'fulfilled' as const, value: [] };
      }
      const normalizedCategoryId = category ? resolveCategoryId(category) : undefined;
      const requestedCanonical =
        normalizedCategoryId != null && normalizedCategoryId !== ''
          ? resolveCanonicalCategoryId(normalizedCategoryId)
          : null;
      // When a category is selected: read only that category's doc id(s) via getDoc + optional
      // documentId prefix query for cross-audit rows. Listing the entire month subcollection fails
      // Firestore rules if the user may read nurse_notes but not ethicSurveys / med_error_incident
      // under the same month (queries are not filters — one unreadable doc denies the whole list).
      // When no category: list the full month (managers / broad views).
      const results = await (async () => {
        try {
          const monthCol = collection(db, 'submissions', firestoreDepartmentId, reportingMonth);
          if (isReportingDebugEnabled()) {
            console.warn('[Report] querying', `submissions/${firestoreDepartmentId}/${reportingMonth}`);
          }

          let allDocs: Array<{ id: string; data: DocumentData }>;

          if (normalizedCategoryId) {
            const docIds = submissionCategoryDocIdsToTry(normalizedCategoryId);
            const getDocAttempts = await Promise.all(
              docIds.map(async (docId) => {
                const docPath = `submissions/${firestoreDepartmentId}/${reportingMonth}/${docId}`;
                try {
                  const ref = doc(db, 'submissions', firestoreDepartmentId, reportingMonth, docId);
                  const snap = await getDoc(ref);
                  if (!snap.exists()) {
                    return { docId, docPath, status: 'missing' as const };
                  }
                  return { docId, docPath, status: 'found' as const, id: snap.id, data: snap.data() as DocumentData };
                } catch (error) {
                  const errorDetails = error as { code?: string; message?: string };
                  return {
                    docId,
                    docPath,
                    status: 'error' as const,
                    code: errorDetails?.code ?? null,
                    message: errorDetails?.message ?? String(error),
                  };
                }
              }),
            );
            const permissionDeniedAttempt = getDocAttempts.find(
              (attempt) => attempt.status === 'error' && attempt.code === 'permission-denied',
            );
            if (permissionDeniedAttempt && getDocAttempts.every((a) => a.status !== 'found')) {
              const err = new Error(permissionDeniedAttempt.message ?? 'Missing or insufficient permissions.');
              (err as { code?: string }).code = 'permission-denied';
              throw err;
            }
            allDocs = getDocAttempts
              .filter((attempt) => attempt.status === 'found')
              .map((attempt) => ({ id: attempt.id, data: attempt.data }));
            if (import.meta.env.DEV || isReportingDebugEnabled()) {
              const missingDocPaths = getDocAttempts
                .filter((attempt) => attempt.status === 'missing')
                .map((attempt) => attempt.docPath);
              const erroredAttempts = getDocAttempts
                .filter((attempt) => attempt.status === 'error')
                .map((attempt) => ({
                  docPath: attempt.docPath,
                  code: attempt.code,
                  message: attempt.message,
                }));
              console.warn('[Report trace] targeted getDoc attempts', {
                departmentId: firestoreDepartmentId,
                reportingMonth,
                category: normalizedCategoryId,
                attemptedCount: getDocAttempts.length,
                foundCount: allDocs.length,
                missingCount: missingDocPaths.length,
                errorCount: erroredAttempts.length,
                missingDocPaths,
                erroredAttempts,
              });
            }

            if (requestedCanonical === 'nurse_notes') {
              try {
                const crossQ = query(
                  monthCol,
                  orderBy(documentId()),
                  startAt('CROSS_AUDIT_NURSING'),
                  endAt('CROSS_AUDIT_NURSING\uf8ff'),
                );
                const crossSnap = await getDocs(crossQ);
                const seen = new Set(allDocs.map((d) => d.id));
                crossSnap.docs.forEach((docSnap) => {
                  if (seen.has(docSnap.id)) return;
                  seen.add(docSnap.id);
                  allDocs.push({ id: docSnap.id, data: docSnap.data() });
                });
              } catch (crossErr) {
                if (import.meta.env.DEV || isReportingDebugEnabled()) {
                  console.warn('[Report] cross-audit prefix query failed (non-fatal)', {
                    departmentId: firestoreDepartmentId,
                    reportingMonth,
                    error: crossErr instanceof Error ? crossErr.message : String(crossErr),
                    code: (crossErr as { code?: string })?.code,
                  });
                }
              }
            }
          } else {
            const monthSnapshot = await getDocs(monthCol);
            allDocs = monthSnapshot.docs.map((docSnap) => ({
              id: docSnap.id,
              data: docSnap.data(),
            }));
          }

          // Prefer non-legacy; fall back to legacy parent when non-legacy is an empty stub (e.g. Sheet import).
          allDocs = filterReportingDocsPreferringUsableContent(
            allDocs.map((d) => ({ id: d.id, data: d.data as Record<string, unknown> })),
          ).map((d) => ({ id: d.id, data: d.data as DocumentData }));

          if (import.meta.env.DEV || isReportingDebugEnabled()) {
            console.info('Report fetch month snapshot', {
              departmentId: firestoreDepartmentId,
              reportingMonth,
              categoryMode: normalizedCategoryId ? 'targeted-getDoc' : 'list-month',
              docsLength: allDocs.length,
              docIds: allDocs.slice(0, 20).map((d) => d.id),
            });
            console.info('Report fetch after legacy filter', {
              departmentId: firestoreDepartmentId,
              reportingMonth,
              allDocsLength: allDocs.length,
              allDocsSampleIds: allDocs.slice(0, 10).map((d) => d.id),
            });
          }

          // Fallback: when department path is empty (e.g. data only in users/uid/submissions), query collectionGroup
          if (allDocs.length === 0) {
            if (import.meta.env.DEV || isReportingDebugEnabled()) {
              console.info('Report fetch collectionGroup fallback triggered', {
                departmentId: firestoreDepartmentId,
                reportingMonth,
              });
            }
            const tryCollectionGroup = async (month: ReportingMonth): Promise<typeof allDocs> => {
              const cgRef = collectionGroup(db, 'submissions');
              const categoryWhere =
                normalizedCategoryId != null && normalizedCategoryId !== ''
                  ? [where('categoryId', '==', normalizedCategoryId)]
                  : [];
              const cgQuery = query(
                cgRef,
                where('departmentId', '==', firestoreDepartmentId),
                where('reportingMonth', '==', month),
                ...categoryWhere,
              );
              const cgSnapshot = await getDocs(cgQuery);
              return filterReportingDocsPreferringUsableContent(
                cgSnapshot.docs.map((docSnap) => ({
                  id: docSnap.id,
                  data: docSnap.data() as Record<string, unknown>,
                })),
              ).map((d) => ({ id: d.id, data: d.data as DocumentData }));
            };
            try {
              allDocs = await tryCollectionGroup(reportingMonth);
              // If still empty, try alternate format (e.g. 2026-2 vs 2026-02) for legacy data
              if (allDocs.length === 0) {
                const altMonth = getAlternateReportingMonth(reportingMonth);
                if (altMonth) {
                  allDocs = await tryCollectionGroup(altMonth);
                  if (allDocs.length > 0 && (import.meta.env.DEV || isReportingDebugEnabled())) {
                    console.info('Report fetch collectionGroup fallback found docs with alternate month format', {
                      departmentId: firestoreDepartmentId,
                      requestedMonth: reportingMonth,
                      alternateMonth: altMonth,
                      count: allDocs.length,
                    });
                  }
                }
              }
            } catch (cgErr) {
              const cgCode = (cgErr as { code?: string })?.code;
              if (import.meta.env.DEV || isReportingDebugEnabled()) {
                console.warn('Report fetch collectionGroup fallback error', {
                  departmentId: firestoreDepartmentId,
                  reportingMonth,
                  error: cgErr instanceof Error ? cgErr.message : String(cgErr),
                  code: cgCode,
                });
              }
              if (cgCode === 'permission-denied' || cgCode === 'failed-precondition') {
                if (!normalizedCategoryId) {
                  throw cgErr;
                }
                allDocs = [];
              }
            }
          }

          if (!normalizedCategoryId) {
            return allDocs.map((d) =>
              normalizeDepartmentSubmissionDoc({ id: d.id, data: () => d.data }, reportingMonth),
            );
          }

          const filtered = allDocs.filter((d) => {
            if (!requestedCanonical) return false;
            const docId = d.id;
            const data = d.data;
            const docCategoryId = data?.categoryId ?? data?.category ?? docId;
            const docSourceCategoryId = data?.sourceCategoryId;
            const docSourceCategoryTitle = data?.sourceCategoryTitle;
            const docCategoryTitle = data?.categoryTitle;
            const docCanonicals = [
              resolveCanonicalCategoryId(docCategoryId),
              resolveCanonicalCategoryId(docSourceCategoryId),
              resolveCanonicalCategoryId(docSourceCategoryTitle),
              resolveCanonicalCategoryId(docCategoryTitle),
              resolveCanonicalCategoryId(docId),
            ].filter((c): c is string => c != null);
            return (
              docCanonicals.includes(requestedCanonical) ||
              (docId.startsWith('CROSS_AUDIT_NURSING') && requestedCanonical === 'nurse_notes')
            );
          });

          if (import.meta.env.DEV || isReportingDebugEnabled()) {
            const firstDoc = allDocs[0];
            const fd = firstDoc?.data;
            const sampleCanonicals = fd
              ? [
                  resolveCanonicalCategoryId(fd.categoryId ?? fd.category ?? firstDoc!.id),
                  resolveCanonicalCategoryId(fd.sourceCategoryId as string | undefined),
                  resolveCanonicalCategoryId(fd.sourceCategoryTitle as string | undefined),
                  resolveCanonicalCategoryId(fd.categoryTitle as string | undefined),
                  resolveCanonicalCategoryId(firstDoc!.id),
                ].filter((c): c is string => c != null)
              : [];
            console.info('Report fetch category filter', {
              departmentId: firestoreDepartmentId,
              reportingMonth,
              requestedCanonical,
              allDocsLength: allDocs.length,
              filteredLength: filtered.length,
              sampleDocCanonicals: sampleCanonicals,
            });
          }

          return filtered.map((d) =>
            normalizeDepartmentSubmissionDoc({ id: d.id, data: () => d.data }, reportingMonth),
          );
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err);
          const code = (err as { code?: string })?.code;
          const isNetworkError =
            msg.includes('ERR_BLOCKED_BY_CLIENT') ||
            msg.includes('Failed to fetch') ||
            msg.includes('NetworkError') ||
            msg.includes('Load failed') ||
            (err instanceof Error && err.name === 'TypeError' && msg.toLowerCase().includes('fetch'));
          if (isNetworkError) throw err;
          if (code === 'permission-denied' || code === 'failed-precondition') {
            throw err;
          }
          if (import.meta.env.DEV || isReportingDebugEnabled()) {
            console.warn('[Report fetch] primary query error (silently returning [])', {
              departmentId: firestoreDepartmentId,
              reportingMonth,
              category: normalizedCategoryId ?? null,
              errorCode: code,
              errorMessage: msg,
            });
          }
          const queryKey = `${firestoreDepartmentId}|${reportingMonth}|${normalizedCategoryId ?? 'all'}|${code ?? msg}`;
          if (!seenErrors.has(queryKey)) {
            seenErrors.add(queryKey);
            console.warn(
              `[Report] submissions query failed: ${code ?? msg} dept=${firestoreDepartmentId} month=${reportingMonth} category=${normalizedCategoryId ?? 'all'}`,
            );
          }
          return [];
        }
      })();

      if (import.meta.env.DEV || isReportingDebugEnabled()) {
        const firstDoc = results[0];
        const hasMetrics = Boolean(firstDoc?.metrics && Object.keys(firstDoc.metrics).length > 0);
        const crossAuditCount = results.filter((r) => r.isCrossAudit || (r as any).crossAudit).length;
        console.info('Report fetch department docs', {
          departmentId: firestoreDepartmentId,
          reportingMonth,
          category: normalizedCategoryId ?? null,
          docsLength: results.length,
          crossAuditCount,
          firstDocId: firstDoc?.id ?? null,
          hasMetrics,
        });
      }

      return { status: 'fulfilled' as const, value: results };
      } catch (reason) {
        return { status: 'rejected' as const, reason };
      }
    },
  );

  // Also fetch cross audit submissions where this department is the audited department
  // Cross audits may be stored under the auditor's department path, so we need to query globally
  // Use same mapped IDs as path query (handles LINE_OF_WORK_DEPARTMENT_MAP vs Firestore doc ID mismatch)
  if (import.meta.env.DEV || isReportingDebugEnabled()) {
    if (departmentIds.length !== uniqueMappedIds.length) {
      console.info('Cross-audit department ID mapping', {
        queryDepartmentIds: departmentIds,
        mappedDepartmentIds: uniqueMappedIds,
        mappingChanged: departmentIds.length !== uniqueMappedIds.length,
      });
    }
  }
  
  const crossAuditSubmissionsByDepartment = await Promise.allSettled(
    uniqueMappedIds.map(async (firestoreDepartmentId) => {
      if (isInvalidDepartmentId(firestoreDepartmentId)) {
        return [];
      }
      const normalizedCategoryId = category ? resolveCategoryId(category) : undefined;

      // Cross-audit nursing docs are written under the audited ward path
      // (`submissions/{auditedDept}/{month}/CROSS_AUDIT_*`) with `departmentId` = audited dept only —
      // they often omit `auditedDepartmentId`. Legacy/alternate storage may use `auditedDepartmentId`
      // when the doc lives under the auditor's tree. Run both collection-group shapes and merge.
      const submissionCollection = collectionGroup(db, 'submissions');
      const monthVariants = Array.from(
        new Set(
          [reportingMonth, getAlternateReportingMonth(reportingMonth)].filter(
            (m): m is ReportingMonth => m != null && m !== '',
          ),
        ),
      );
      const deptFieldSpecs = ['auditedDepartmentId', 'departmentId'] as const;

      const mergedByPath = new Map<string, QueryDocumentSnapshot>();

      const runOneCrossAuditQuery = async (
        deptField: (typeof deptFieldSpecs)[number],
        month: ReportingMonth,
      ): Promise<void> => {
        try {
          const snapshot = await getDocs(
            query(
              submissionCollection,
              where(deptField, '==', firestoreDepartmentId),
              where('reportingMonth', '==', month),
              where('crossAudit', '==', true),
              ...(normalizedCategoryId != null && normalizedCategoryId !== ''
                ? [where('categoryId', '==', normalizedCategoryId)]
                : []),
            ),
          );
          snapshot.docs.forEach((d) => mergedByPath.set(d.ref.path, d));
        } catch (error) {
          const errorDetails = error as { code?: string; message?: string };
          if (errorDetails?.code === 'permission-denied' || errorDetails?.code === 'failed-precondition') {
            if (import.meta.env.DEV || isReportingDebugEnabled()) {
              console.info('Failed to fetch cross audit submissions (permission/index issue)', {
                departmentId: firestoreDepartmentId,
                reportingMonth,
                queryDeptField: deptField,
                queryMonth: month,
                category: normalizedCategoryId ?? null,
                errorCode: errorDetails?.code ?? null,
                errorMessage: errorDetails?.message ?? null,
              });
            }
            return;
          }
          throw error;
        }
      };

      try {
        for (const month of monthVariants) {
          for (const deptField of deptFieldSpecs) {
            await runOneCrossAuditQuery(deptField, month);
          }
        }

        const combinedDocs = Array.from(mergedByPath.values());

        if (normalizedCategoryId) {
          const requestedCanonical = resolveCanonicalCategoryId(normalizedCategoryId);
          const docs = combinedDocs
            .filter((docSnap) => (docSnap.data() as { legacy?: boolean })?.legacy !== true)
            .map((docSnap) => {
              if (!requestedCanonical) return null;
              const data = docSnap.data();
              const docCategoryId = data.categoryId ?? data.category ?? docSnap.id;
              const docSourceCategoryId = data.sourceCategoryId;
              const docSourceCategoryTitle = data.sourceCategoryTitle;
              const docCategoryTitle = data.categoryTitle;
              const docId = docSnap.id;
              const docCanonicals = [
                resolveCanonicalCategoryId(docCategoryId),
                resolveCanonicalCategoryId(docSourceCategoryId),
                resolveCanonicalCategoryId(docSourceCategoryTitle),
                resolveCanonicalCategoryId(docCategoryTitle),
                resolveCanonicalCategoryId(docId),
              ].filter((c): c is string => c != null);
              const matchesCategory =
                docCanonicals.includes(requestedCanonical) ||
                (docId.startsWith('CROSS_AUDIT_NURSING') && requestedCanonical === 'nurse_notes');
              if (!matchesCategory) return null;
              return normalizeDepartmentSubmissionDoc({ id: docSnap.id, data: () => data }, reportingMonth);
            })
            .filter((submission): submission is ReportingSubmission => submission !== null);
          return docs;
        }

        return combinedDocs
          .filter((docSnap) => (docSnap.data() as { legacy?: boolean })?.legacy !== true)
          .map((docSnap) =>
            normalizeDepartmentSubmissionDoc({ id: docSnap.id, data: () => docSnap.data() }, reportingMonth),
          );
      } catch (error) {
        const errorDetails = error as { code?: string; message?: string };
        if (errorDetails?.code === 'permission-denied' || errorDetails?.code === 'failed-precondition') {
          if (import.meta.env.DEV || isReportingDebugEnabled()) {
            console.info('Failed to fetch cross audit submissions (permission/index issue)', {
              departmentId: firestoreDepartmentId,
              reportingMonth,
              category: normalizedCategoryId ?? null,
              errorCode: errorDetails?.code ?? null,
              errorMessage: errorDetails?.message ?? null,
            });
          }
          return [];
        }
        throw error;
      }
    }),
  );

  const uid = getApps().length ? getAuth().currentUser?.uid ?? null : null;
  const role =
    roleContext?.generalRole ??
    roleContext?.positionRole ??
    roleContext?.departmentRole ??
    roleContext?.teamRole ??
    roleContext?.teamRoles?.[0] ??
    null;

  const submissions: ReportingSubmission[] = [];
  const reportingErrors: Array<{
    code: string;
    message: string;
    departmentId: string;
    firestorePath: string;
  }> = [];

  submissionsByDepartment.forEach((result, index) => {
    if (result.status === 'fulfilled') {
      submissions.push(...result.value);
      return;
    }

    const errorDetails = result.reason as { code?: string; message?: string };
    const departmentId = uniqueMappedIds[index] ?? departmentIds[index];
    const firestorePath = `submissions/${getFirestorePathDepartmentId(departmentId)}/${reportingMonth}`;
    const errorCode = errorDetails?.code ?? 'unknown';
    const errorKey = `${errorCode}|${firestorePath}|${category ?? 'all'}`;

    // Track error for reporting
    reportingErrors.push({
      code: errorCode,
      message: errorDetails?.message ?? 'Unknown Firestore error',
      departmentId,
      firestorePath,
    });

    // Only log errors that haven't been seen before
    if (!seenErrors.has(errorKey)) {
      seenErrors.add(errorKey);
      
      // Permission-denied errors are expected when user doesn't have access to a department
      // Only log them in debug mode or if they're unexpected (e.g., user should have access)
      const isPermissionDenied = errorCode === 'permission-denied';
      const isFailedPrecondition = errorCode === 'failed-precondition';
      const shouldLogError = 
        !isPermissionDenied || // Always log non-permission errors
        (import.meta.env.DEV || isReportingDebugEnabled()); // Log permission errors in debug mode

      if (shouldLogError) {
        if (isPermissionDenied || isFailedPrecondition) {
          // Use console.info for expected permission/index issues
          console.info('Failed to fetch department submissions (permission/index issue)', {
            departmentId: getFirestorePathDepartmentId(departmentId),
            reportingMonth,
            category,
            uid,
            role,
            firestorePath,
            errorCode,
            errorMessage: errorDetails?.message ?? null,
          });
        } else {
          // Use console.error for unexpected errors
          console.error('Failed to fetch department submissions', {
            departmentId: getFirestorePathDepartmentId(departmentId),
            reportingMonth,
            category,
            uid,
            role,
            firestorePath,
            errorCode,
            errorMessage: errorDetails?.message ?? null,
          });
        }
      }
    }
  });

  // Add cross audit submissions
  crossAuditSubmissionsByDepartment.forEach((result) => {
    if (result.status === 'fulfilled') {
      submissions.push(...result.value);
    }
    // Cross audit query errors are already handled in the try-catch above
  });

  // Deduplicate submissions (same submission might appear from both regular and cross audit queries)
  // For cross-audits, use a stable key based on auditedDepartmentId + reportingMonth + categoryId
  // since the same cross-audit can appear with different document IDs from different query paths
  const submissionMap = new Map<string, ReportingSubmission>();
  submissions.forEach((submission) => {
    // Use submissionId if available (consistent across both storage locations)
    // For cross-audits without submissionId, use auditedDepartmentId + reportingMonth + categoryId
    let key: string;
    // Access submissionId from the object - it should be set by normalizeDepartmentSubmissionDoc
    // Also check the raw data property as a fallback (defensive check)
    const submissionIdValue = submission.submissionId ?? (submission as any).submissionId;
    // Ensure we have a string value and it's not empty
    const submissionIdForKey = (submissionIdValue && typeof submissionIdValue === 'string' && submissionIdValue.trim()) 
      ? submissionIdValue.trim() 
      : undefined;
    
    if (submission.isCrossAudit) {
      // Normalize all key components to ensure consistency
      const auditedDeptId = (submission.auditedDepartmentId ?? submission.departmentId ?? 'unknown').trim();
      const reportingKey = (submission.reportingMonth ?? 'unknown-month').toString().trim();
      const docId = (submission.id ?? '').toString().trim();
      // Try submissionId first, fallback to doc id (includes slot: CROSS_AUDIT_NURSING__auditor**userId**slot)
      // Must include slot so chart 1, 2, 3 don't overwrite each other (categoryKey alone would dedupe them)
      if (submissionIdForKey) {
        key = `${submissionIdForKey}::${auditedDeptId}::${reportingKey}`;
      } else {
        key = `cross-audit::${auditedDeptId}::${reportingKey}::${docId || `slot-${(submission as any).crossAuditSlot ?? 'unknown'}`}`;
      }
    } else {
      // Normalize all key components to ensure consistency
      const departmentKey = (submission.departmentId ?? 'unknown-department').trim();
      const reportingKey = (submission.reportingMonth ?? 'unknown-month').toString().trim();
      if (submissionIdForKey) {
        key = `${submissionIdForKey}::${departmentKey}::${reportingKey}`;
      } else {
        key = `${submission.id.trim()}::${departmentKey}::${reportingKey}`;
      }
    }
    // Prefer cross audit version if both exist (shouldn't happen, but just in case)
    if (!submissionMap.has(key) || submission.isCrossAudit) {
      submissionMap.set(key, submission);
    }
  });

  const deduplicatedSubmissions = Array.from(submissionMap.values());

  if (import.meta.env.DEV || isReportingDebugEnabled()) {
    const totalCross = deduplicatedSubmissions.filter((s) => s.isCrossAudit || (s as any).crossAudit).length;
    console.info('[Report trace] fetchDepartmentPathDocs result', {
      reportingMonth,
      category: category ?? null,
      totalSubmissions: deduplicatedSubmissions.length,
      crossAuditCount: totalCross,
      departmentIdsQueried: departmentIds,
      mappedDepartmentIds: uniqueMappedIds,
      pathQueried: uniqueMappedIds.map((id) => `submissions/${id}/${reportingMonth}`),
      permissionErrorsCount: reportingErrors.length,
    });
  }

  // Log summary of permission-denied errors if there are many
  const permissionDeniedErrors = reportingErrors.filter((e) => e.code === 'permission-denied');
  if (permissionDeniedErrors.length > 5 && (import.meta.env.DEV || isReportingDebugEnabled())) {
    const uniqueDepts = new Set(permissionDeniedErrors.map((e) => e.departmentId));
    console.info(
      `Reporting: ${permissionDeniedErrors.length} permission-denied errors across ${uniqueDepts.size} departments (expected when user lacks access).`,
      {
        reportingMonth,
        category,
        role,
        sampleDepartments: Array.from(uniqueDepts).slice(0, 5),
      },
    );
  }

  const anyDepartmentFetchSucceeded = submissionsByDepartment.some(
    (result) => result.status === 'fulfilled',
  );

  if (deduplicatedSubmissions.length === 0 && reportingErrors.length > 0 && !anyDepartmentFetchSucceeded) {
    if (reportingErrors.every((error) => error.code === 'permission-denied')) {
      const uniqueDepts = Array.from(new Set(reportingErrors.map((e) => e.departmentId).filter(Boolean)));
      const sample = uniqueDepts.slice(0, 5).join(', ');
      const suffix = uniqueDepts.length > 5 ? ` (+${uniqueDepts.length - 5} more)` : '';
      const error = new Error(
        `Missing or insufficient permissions. dept=${sample}${suffix} month=${reportingMonth} category=${category ?? 'all'}`,
      );
      (error as { code?: string }).code = 'permission-denied';
      throw error;
    }
    const firstError = reportingErrors[0];
    const error = new Error(
      `Reporting fetch failed (first): ${firstError.code} ${firstError.message} path=${firstError.firestorePath}`,
    );
    (error as { code?: string }).code = firstError.code;
    throw error;
  }

  return deduplicatedSubmissions;
};

const fetchLofSubmissionDocs = async (
  reportingMonth: ReportingMonth,
  lofIds: string[],
  category?: string,
  roleContext?: RoleContext | null,
): Promise<ReportingSubmission[]> => {
  if (!lofIds.length) {
    return [];
  }

  const normalizedCategory = category ? resolveCategoryId(category) : undefined;

  const submissionsByLof = await Promise.all(
    lofIds.map(async (lofId) => {
      try {
        const submissionCollection = collectionGroup(db, 'submissions');
        const snapshot = await getDocs(
          query(
            submissionCollection,
            where('lofId', '==', lofId),
            where('reportingMonth', '==', reportingMonth),
            where('status', '==', 'submitted'),
          ),
        );

        const docs = snapshot.docs
          .filter((docSnap) => (docSnap.data() as { legacy?: boolean })?.legacy !== true)
          .map((docSnap) =>
            normalizeLofSubmissionDoc({ id: docSnap.id, data: () => docSnap.data() }, reportingMonth, lofId),
          );

        if (!category) return docs;

        return docs.filter((submission) => {
          const candidateCategory =
            submission.categoryId ?? submission.categoryTitle ?? submission.category ?? undefined;
          return (
            candidateCategory === category ||
            candidateCategory === normalizedCategory ||
            submission.category === category ||
            submission.categoryTitle === category
          );
        });
      } catch (error) {
        const errorDetails = error as { code?: string; message?: string };
        const uid = getAuth().currentUser?.uid ?? null;
        const role =
          roleContext?.generalRole ??
          roleContext?.positionRole ??
          roleContext?.departmentRole ??
          roleContext?.teamRole ??
          roleContext?.teamRoles?.[0] ??
          null;

        console.error('Failed to fetch LOF submissions', {
          lofId,
          reportingMonth,
          category,
          uid,
          role,
          errorCode: errorDetails?.code ?? null,
          errorMessage: errorDetails?.message ?? null,
        });
        if (
          import.meta.env.DEV &&
          (errorDetails?.code === 'permission-denied' || errorDetails?.code === 'failed-precondition')
        ) {
          console.info('Reporting permission/index issue', {
            lofId,
            reportingMonth,
            category,
            errorCode: errorDetails?.code ?? null,
            errorMessage: errorDetails?.message ?? null,
          });
        }

        throw formatReportingFetchError(errorDetails);
      }
    }),
  );

  return submissionsByLof.flat();
};

const resolveLofIds = (
  departments: DepartmentScopeRecord[],
  roleContext?: RoleContext | null,
  scopedDepartmentIds?: string[] | undefined,
): string[] => {
  const lineOfWorkDepartmentIds = (roleContext?.lineOfWorkDepartmentIds ?? []).filter(Boolean);
  const supervisorScoped = hasSupervisorScope(roleContext);

  if (!supervisorScoped && lineOfWorkDepartmentIds.length === 0) return [];
  if (supervisorScoped && roleContext?.lofId && lineOfWorkDepartmentIds.length === 0) {
    return [roleContext.lofId];
  }

  const baseDepartmentIds = lineOfWorkDepartmentIds.length
    ? lineOfWorkDepartmentIds
    : scopedDepartmentIds && scopedDepartmentIds.length
      ? scopedDepartmentIds
      : [];

  const allowedDepartmentIds = new Set(baseDepartmentIds.filter(Boolean));
  if (supervisorScoped && !allowedDepartmentIds.size) {
    return [];
  }

  const lofIds = new Set<string>();
  departments.forEach((dept) => {
    if (!dept.lofId) return;
    if (allowedDepartmentIds.has(dept.id)) {
      lofIds.add(dept.lofId);
    }
  });

  return Array.from(lofIds);
};

const buildSubmissionKey = (submission: ReportingSubmission): string => {
  const departmentKey = submission.departmentId ?? 'unknown-department';
  const reportingKey = submission.reportingMonth ?? 'unknown-month';
  return `${submission.id}::${departmentKey}::${reportingKey}`;
};

const mergeSubmissions = (...submissionGroups: ReportingSubmission[][]): ReportingSubmission[] => {
  const submissionMap = new Map<string, ReportingSubmission>();
  submissionGroups.flat().forEach((submission) => {
    submissionMap.set(buildSubmissionKey(submission), submission);
  });
  return Array.from(submissionMap.values());
};

/** HTTP POST abort + callable Promise.race — unbounded waits stall year view for many minutes. */
const TEAM_REPORTING_CLOUD_TIMEOUT_MS = 45_000;

/**
 * Nurse-notes team reporting via Cloud Function (Admin SDK), bypassing client Firestore rules.
 * Used for team mode and for normal dashboard when NN hospital-wide widening applies.
 */
const fetchTeamReportingDataCloud = async (
  reportingMonth: ReportingMonth,
  effectiveCategory: string,
): Promise<ReportingAggregateResult | null> => {
  const tryFetchTeamDataHttp = async (urlOverride?: string, forceRefresh = false): Promise<ReportingAggregateResult | null> => {
    const authUser = getAuth(app).currentUser;
    if (!authUser) return null;
    let token: string;
    try {
      token = await authUser.getIdToken(forceRefresh);
    } catch (tokenErr) {
      if (import.meta.env.DEV || isReportingDebugEnabled()) {
        console.warn('[Team reporting cloud] getIdToken failed', { forceRefresh, err: tokenErr });
      }
      return null;
    }
    const projectId = (app.options as { projectId?: string })?.projectId ?? '';
    const url =
      urlOverride ??
      (typeof window !== 'undefined' && window.location?.origin
        ? `${window.location.origin}/api/fetchTeamReportingData`
        : `https://us-central1-${projectId}.cloudfunctions.net/api/fetchTeamReportingData`);
    let res: Response;
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), TEAM_REPORTING_CLOUD_TIMEOUT_MS);
      try {
        res = await fetch(url, {
          method: 'POST',
          signal: controller.signal,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ reportingMonth, category: effectiveCategory }),
        });
      } finally {
        clearTimeout(timeoutId);
      }
    } catch (fetchErr) {
      if (import.meta.env.DEV || isReportingDebugEnabled()) {
        console.warn('[Team reporting cloud] HTTP fetch failed or timed out', {
          aborted: (fetchErr as { name?: string })?.name === 'AbortError',
        });
      }
      return null;
    }
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      if (res.status === 403 && (body?.error?.code === 'permission-denied' || body?.error?.message?.includes('NN'))) {
        if (!forceRefresh) {
          const retry = await tryFetchTeamDataHttp(urlOverride, true);
          if (retry) return retry;
        }
        return {
          submissions: [],
          totals: {},
          averages: {},
          months: [reportingMonth],
          message: `ไม่สามารถเข้าถึงข้อมูล${NURSE_NOTES_TEAM_LABEL}ได้ กรุณาติดต่อผู้ดูแลระบบ`,
        };
      }
      return null;
    }
    let data: {
      submissions?: ReportingSubmission[];
      totals?: Record<string, number>;
      averages?: Record<string, number>;
      months?: string[];
    };
    try {
      data = await res.json();
    } catch {
      return null;
    }
    if (data && Array.isArray(data.submissions)) {
      if (import.meta.env.DEV || isReportingDebugEnabled()) {
        const subs = data.submissions;
        console.info('[Team reporting cloud] Fetched via HTTP api', {
          submissionCount: subs.length,
          departmentCount: new Set(
            subs.map((s: { departmentId?: string; departmentName?: string }) => s.departmentId ?? s.departmentName),
          ).size,
        });
      }
      return {
        submissions: data.submissions as ReportingSubmission[],
        totals: data.totals ?? {},
        averages: data.averages ?? {},
        months: data.months ?? [reportingMonth],
      };
    }
    return null;
  };

  const tryFetchTeamDataCallable = async (): Promise<ReportingAggregateResult | null> => {
    try {
      const functions = getFunctions(app, 'us-central1');
      const fetchTeam = httpsCallable<
        { reportingMonth: string; category?: string },
        { submissions: ReportingSubmission[]; totals: Record<string, number>; averages: Record<string, number>; months: string[] }
      >(functions, 'fetchTeamReportingData');
      const callPromise = fetchTeam({ reportingMonth, category: effectiveCategory });
      const result = await Promise.race([
        callPromise,
        new Promise<undefined>((resolve) => setTimeout(() => resolve(undefined), TEAM_REPORTING_CLOUD_TIMEOUT_MS)),
      ]);
      if (result === undefined) {
        if (import.meta.env.DEV || isReportingDebugEnabled()) {
          console.warn('[Team reporting cloud] Callable timed out', {
            reportingMonth,
            category: effectiveCategory,
            ms: TEAM_REPORTING_CLOUD_TIMEOUT_MS,
          });
        }
        return null;
      }
      const data = result.data;
      if (data && Array.isArray(data.submissions)) {
        return {
          submissions: data.submissions as ReportingSubmission[],
          totals: data.totals ?? {},
          averages: data.averages ?? {},
          months: data.months ?? [reportingMonth],
        };
      }
    } catch (err) {
      const code = (err as { code?: string })?.code;
      if (code === 'functions/permission-denied' || code === 'permission-denied') {
        return {
          submissions: [],
          totals: {},
          averages: {},
          months: [reportingMonth],
          message: `ไม่สามารถเข้าถึงข้อมูล${NURSE_NOTES_TEAM_LABEL}ได้ กรุณาติดต่อผู้ดูแลระบบ`,
        };
      }
      throw err;
    }
    return null;
  };

  let teamResult: ReportingAggregateResult | null = null;
  const useSameOrigin = typeof window !== 'undefined' && !!window.location?.origin;
  const projectId = (app.options as { projectId?: string })?.projectId ?? '';
  const directApiUrl = projectId
    ? `https://us-central1-${projectId}.cloudfunctions.net/api/fetchTeamReportingData`
    : '';
  try {
    if (useSameOrigin) {
      teamResult = await tryFetchTeamDataHttp();
      // Local Vite has no Hosting /api rewrite unless emulator proxy is on — try Cloud URL next.
      if (!teamResult && directApiUrl) teamResult = await tryFetchTeamDataHttp(directApiUrl);
      if (!teamResult) teamResult = await tryFetchTeamDataCallable();
    } else {
      teamResult = await tryFetchTeamDataCallable();
      if (!teamResult && directApiUrl) teamResult = await tryFetchTeamDataHttp(directApiUrl);
      else if (!teamResult) teamResult = await tryFetchTeamDataHttp();
    }
  } catch (err) {
    const message = (err as { message?: string })?.message ?? String(err);
    if (import.meta.env.DEV || isReportingDebugEnabled()) {
      console.warn('[Team reporting cloud] Primary fetch failed, trying fallback', { message });
    }
    try {
      if (directApiUrl) {
        teamResult = await tryFetchTeamDataHttp(directApiUrl);
      }
      if (!teamResult) teamResult = await tryFetchTeamDataCallable();
      if (!teamResult && directApiUrl) teamResult = await tryFetchTeamDataHttp(directApiUrl);
    } catch (fallbackErr) {
      if (import.meta.env.DEV || isReportingDebugEnabled()) {
        console.warn('[Team reporting cloud] Fallback also failed', { err: fallbackErr });
      }
    }
  }
  return teamResult;
};

async function fetchMonthlyReportingDataUncached(
  reportingMonth: ReportingMonth,
  category?: string,
  roleScope?: RoleScope | null,
  roleContext?: RoleContext | null,
  overrides?: ReportingScopeOverrides,
  options?: ReportingFetchOptions,
): Promise<ReportingAggregateResult> {
  const mode = options?.mode ?? 'default';
  const normalizedCategory = category ? resolveCategoryId(category) : undefined;

  if (mode === 'team') {
    const teamAllowedCategoryIds = (options?.teamAllowedCategoryIds ?? []).filter(Boolean);
    
    if (
      normalizedCategory &&
      teamAllowedCategoryIds.length > 0 &&
      !teamAllowedCategoryIds.includes(normalizedCategory)
    ) {
      return {
        submissions: [],
        totals: {},
        averages: {},
        months: [reportingMonth],
        message: 'Not authorized for this category',
      };
    }

    const effectiveCategory = normalizedCategory ?? teamAllowedCategoryIds[0] ?? 'nurse_notes';
    if (!effectiveCategory) {
      return {
        submissions: [],
        totals: {},
        averages: {},
        months: [reportingMonth],
        message: 'No team categories available',
      };
    }

    const teamResult = await fetchTeamReportingDataCloud(reportingMonth, effectiveCategory);
    if (teamResult) {
      if (import.meta.env.DEV || isReportingDebugEnabled()) {
        console.info('[Team Mode] Fetched via Cloud Function', {
          submissionCount: teamResult.submissions.length,
          departmentCount: new Set(teamResult.submissions.map((s) => s.departmentId ?? s.departmentName)).size,
        });
      }
      return teamResult;
    }

    return {
      submissions: [],
      totals: {},
      averages: {},
      months: [reportingMonth],
      message: `ไม่สามารถเข้าถึงข้อมูล${NURSE_NOTES_TEAM_LABEL}ได้ กรุณาติดต่อผู้ดูแลระบบ`,
    };
  }

  const allowedCategoryIds = (roleContext?.allowedCategoryIds ?? []).filter(Boolean);
  if (normalizedCategory && allowedCategoryIds.length && !allowedCategoryIds.includes(normalizedCategory)) {
    return {
      submissions: [],
      totals: {},
      averages: {},
      months: [reportingMonth],
      message: 'Not authorized for this category',
    };
  }

  const teamHospitalWide =
    !!normalizedCategory && hasTeamCategoryHospitalWideAccess(roleContext, category);

  let departmentFilters = resolveDepartmentFilters(roleScope, overrides);
  // NN / ethic hospital-wide roles default to all departments, but sticky/report overrides may narrow by explicit IDs (e.g. nurse_notes shell filter).
  if (teamHospitalWide) {
    const overrideDeptIds =
      overrides && 'departmentIds' in overrides && Array.isArray(overrides.departmentIds)
        ? (overrides.departmentIds as string[]).filter(Boolean)
        : [];
    const hasExplicitDeptSubset =
      overrideDeptIds.length > 0 && !overrideDeptIds.includes('*');
    if (!hasExplicitDeptSubset) {
      departmentFilters = undefined;
    }
  }
  const lofOverrides = resolveLofOverrides(overrides);
  const supervisorScoped = hasSupervisorScope(roleContext);
  let effectiveSupervisorDeptIds = (roleContext?.lineOfWorkDepartmentIds ?? []).filter(Boolean);
  const shouldIncludeLofSubmissions = supervisorScoped || Boolean(lofOverrides);

  if (import.meta.env.DEV || isReportingDebugEnabled()) {
    console.info('[Report trace] fetchMonthlyReportingData scope inputs', {
      reportingMonth,
      category: normalizedCategory ?? null,
      roleScopeDepartmentIds: roleScope?.departmentIds ?? roleScope?.view?.departmentIds ?? [],
      overridesDepartmentIds: overrides && 'departmentIds' in overrides ? overrides.departmentIds : null,
      overridesLofIds: overrides && 'lofIds' in overrides ? overrides.lofIds : null,
      departmentFilters,
      teamHospitalWide,
      lofOverrides,
      supervisorScoped,
      roleContextLofId: roleContext?.lofId ?? null,
      roleContextLineOfWorkDepartmentIds: roleContext?.lineOfWorkDepartmentIds ?? [],
      effectiveSupervisorDeptIdsInitial: effectiveSupervisorDeptIds,
    });
  }

  // Wildcard (all departments): global roles, overrides '*', or team_role NN/ETHIC for nurse_notes/ethicSurveys.
  const hasRoleScopeWildcard =
    departmentFilters === undefined &&
    (overrides?.departmentIds?.includes('*') ||
      roleScope?.view?.departmentIds?.includes('*') ||
      roleScope?.departmentIds?.includes('*'));
  const hasWildcardAccess = hasRoleScopeWildcard || teamHospitalWide;

  // Skip early return when supervisor has LOF: we will resolve departments from LOF below and show their submissions.
  const hasLofKeyForFetch =
    Boolean(roleContext?.lofId?.trim()) ||
    Boolean(roleContext?.lofIds?.some((id) => typeof id === 'string' && id.trim()));
  const supervisorCanResolveLof =
    supervisorScoped && (hasLofKeyForFetch || Boolean(lofOverrides && lofOverrides.length > 0));
  const willEarlyReturn =
    Boolean(departmentFilters) &&
    departmentFilters!.length === 0 &&
    (!shouldIncludeLofSubmissions || (lofOverrides && lofOverrides.length === 0)) &&
    !supervisorCanResolveLof;

  if (import.meta.env.DEV || isReportingDebugEnabled()) {
    console.info('Report fetch pre-early-return', {
      departmentFilters,
      departmentFiltersLength: departmentFilters?.length ?? null,
      shouldIncludeLofSubmissions,
      supervisorCanResolveLof,
      willEarlyReturn,
    });
  }

  if (willEarlyReturn) {
    return { submissions: [], totals: {}, averages: {}, months: [reportingMonth] };
  }

  const canonicalForHospitalWide =
    normalizedCategory != null && normalizedCategory !== ''
      ? resolveCanonicalCategoryId(normalizedCategory) ?? normalizedCategory
      : undefined;
  // Run before fetchDepartments: NN cloud needs no department catalog; yearly view fires many months in parallel otherwise.
  if (mode === 'default' && teamHospitalWide && canonicalForHospitalWide === 'nurse_notes') {
    const hasExplicitDeptSubset =
      Array.isArray(departmentFilters) &&
      departmentFilters.length > 0 &&
      !departmentFilters.includes('*');
    /**
     * Explicit department filter + detail mode (ReportingTable month rows): bypass NN cloud cache.
     * Stale cloud payloads keep the empty `nurse_notes` Sheet stub (0%) and drop the legacy Thai doc (~95%).
     * Direct department-path reads use {@link filterReportingDocsPreferringUsableContent}.
     */
    const bypassNnCloudForFilteredDetail =
      hasExplicitDeptSubset && (options?.fetchMode ?? 'detail') === 'detail';

    if (!bypassNnCloudForFilteredDetail) {
      const rawPayload = await getNnNurseNotesRawPayload(reportingMonth);
      if (rawPayload) {
        const submissionsFiltered = filterReportingSubmissionsByClientDepartments(
          rawPayload.submissions,
          departmentFilters?.length ? departmentFilters : undefined,
        );
        const { totals, averages } = computeAggregates(submissionsFiltered);
        return {
          submissions: submissionsFiltered,
          totals,
          averages,
          months: rawPayload.months.length ? rawPayload.months : [reportingMonth],
          ...(rawPayload.message ? { message: rawPayload.message } : {}),
        };
      }
      return {
        submissions: [],
        totals: {},
        averages: {},
        months: [reportingMonth],
        message:
          'ไม่สามารถโหลดข้อมูลบันทึกทางการพยาบาลจากระบบคณะทำงานได้ กรุณาลองใหม่หรือติดต่อผู้ดูแลระบบ',
      };
    }
  }

  if (import.meta.env.DEV || isReportingDebugEnabled()) {
    console.info('Report fetch early return skipped, proceeding with fetch', {
      departmentFilters,
      departmentFiltersLength: departmentFilters?.length ?? null,
    });
  }

  // Always fetch for mapping canonical IDs (e.g. LINE_OF_WORK_DEPARTMENT_MAP) to actual Firestore doc IDs
  const departmentRecords = await fetchDepartments();
  if (supervisorScoped) {
    // Prefer roleContext.lofId / lofIds (from AuthProvider); fall back to lofOverrides when roleScope/roleContext
    // don't have LOF department IDs (e.g. async loading, or user has lofRef but lofId not yet in roleContext)
    if (hasLofKeyForFetch) {
      const lofDeptIds = await fetchSupervisorDepartmentIdsFromLof(roleContext);
      if (lofDeptIds.length > 0) {
        effectiveSupervisorDeptIds = Array.from(new Set([...effectiveSupervisorDeptIds, ...lofDeptIds]));
      }
    }
    if (effectiveSupervisorDeptIds.length === 0 && lofOverrides && lofOverrides.length > 0) {
      const lofDeptIds = await fetchDepartmentIdsByLofIds(lofOverrides);
      if (lofDeptIds.length > 0) {
        effectiveSupervisorDeptIds = lofDeptIds;
      }
    }
  }
  const allDeptIds = departmentRecords.map((department) => department.id);
  
  // When departmentFilters is undefined, only use all departments if user has wildcard access
  // Otherwise, fall back to their actual scope (lineOfWorkDepartmentIds or roleScope)
  // When departmentFilters is an empty array [] (e.g. supervisor_nurse with LOF but roleScope has []),
  // use effectiveSupervisorDeptIds if populated (from fetchSupervisorDepartmentIdsFromLof)
  const departmentIds =
    departmentFilters === undefined
      ? hasWildcardAccess
        ? allDeptIds
        : effectiveSupervisorDeptIds.length > 0
          ? effectiveSupervisorDeptIds
          : (roleScope?.view?.departmentIds?.filter((id) => id !== '*') ??
             roleScope?.departmentIds?.filter((id) => id !== '*') ?? [])
      : departmentFilters.length > 0
        ? departmentFilters
        : // Empty array - use LOF depts for supervisor if available, else roleScope
          (supervisorScoped && effectiveSupervisorDeptIds.length > 0
            ? effectiveSupervisorDeptIds
            : (roleScope?.view?.departmentIds?.filter((id) => id !== '*') ??
               roleScope?.departmentIds?.filter((id) => id !== '*') ?? []));
  if (import.meta.env.DEV || isReportingDebugEnabled()) {
    console.info('[Report trace] resolved departmentIds for fetch', {
      departmentIds,
      departmentIdsCount: departmentIds.length,
      generalRole: roleContext?.generalRole ?? null,
      positionRole: roleContext?.positionRole ?? null,
      lofId: roleContext?.lofId ?? null,
      effectiveSupervisorDeptIds,
    });
  }
  if (import.meta.env.DEV || isReportingDebugEnabled()) {
    console.info('Reporting department filters', {
      supervisorScoped,
      supervisorDeptIdsCount: effectiveSupervisorDeptIds.length,
      departmentFilters,
      departmentFiltersLength: departmentFilters?.length ?? null,
      departmentFiltersIsUndefined: departmentFilters === undefined,
      hasWildcardAccess,
      departmentIdsCount: departmentIds.length,
      generalRole: roleContext?.generalRole ?? null,
      positionRole: roleContext?.positionRole ?? null,
      lofId: roleContext?.lofId ?? null,
      lineOfWorkDepartmentIdsCount: roleContext?.lineOfWorkDepartmentIds?.length ?? 0,
      roleScopeViewDeptIds: roleScope?.view?.departmentIds ?? null,
      roleScopeDeptIds: roleScope?.departmentIds ?? null,
      effectiveSupervisorDeptIds: effectiveSupervisorDeptIds.slice(0, 5),
      departmentIdsSample: departmentIds.slice(0, 10),
      reportingMonth,
      category: normalizedCategory,
    });
  }

  if (import.meta.env.DEV || isReportingDebugEnabled()) {
    console.info('Reporting department scope context', {
      generalRole: roleContext?.generalRole ?? null,
      positionRole: roleContext?.positionRole ?? null,
      viewDepartmentIds: roleScope?.view?.departmentIds ?? null,
      lofId: roleContext?.lofId ?? null,
      lineOfWorkDepartmentIdsCount: roleContext?.lineOfWorkDepartmentIds?.length ?? 0,
      resolvedDepartmentIdsCount: departmentIds.length,
      effectiveSupervisorDeptIdsCount: effectiveSupervisorDeptIds.length,
      resolvedDepartmentIdsSample: departmentIds.slice(0, 10),
    });
  }

  const [departmentSubmissions, lofSubmissions] = await Promise.all([
    fetchDepartmentPathDocs(reportingMonth, departmentIds, category, roleContext, departmentRecords),
    shouldIncludeLofSubmissions
      ? fetchLofSubmissionDocs(
          reportingMonth,
          lofOverrides ?? resolveLofIds(departmentRecords, roleContext, departmentFilters),
          category,
          roleContext,
        )
      : Promise.resolve([]),
  ]);

  const submissions = mergeSubmissions(departmentSubmissions, lofSubmissions);
  const { totals, averages } = computeAggregates(submissions);

  if (submissions.length === 0 && (import.meta.env.DEV || isReportingDebugEnabled())) {
    console.info('[Report fetch] 0 submissions returned — trace', {
      reportingMonth,
      category: normalizedCategory ?? null,
      departmentIdsCount: departmentIds.length,
      departmentIdsSample: departmentIds.slice(0, 15),
      firestorePaths: departmentIds.slice(0, 5).map(
        (deptId) => `submissions/${getFirestorePathDepartmentId(deptId) ?? deptId}/${reportingMonth}/${normalizedCategory ?? '*'}`,
      ),
      departmentSubmissionsCount: departmentSubmissions.length,
      lofSubmissionsCount: lofSubmissions.length,
      hint: 'Check: (1) docs exist at submissions/{deptId}/{month}/{category} (2) category matches (3) departmentIds in scope',
    });
  }

  return {
    submissions,
    totals,
    averages,
    months: [reportingMonth],
  };
}

function finalizeMonthlyReportingResult(
  reportingMonth: ReportingMonth,
  category: string | undefined,
  overrides: ReportingScopeOverrides | undefined,
  options: ReportingFetchOptions | undefined,
  result: ReportingAggregateResult,
): ReportingAggregateResult {
  rememberReportingSubmissionsBody(
    reportingMonth,
    category,
    overrides,
    options,
    result.submissions,
  );
  return result;
}

export const fetchMonthlyReportingData = async (
  reportingMonth: ReportingMonth,
  category?: string,
  roleScope?: RoleScope | null,
  roleContext?: RoleContext | null,
  overrides?: ReportingScopeOverrides,
  options?: ReportingFetchOptions,
): Promise<ReportingAggregateResult> => {
  const uid = getAuth(app).currentUser?.uid ?? '';
  const mode = options?.mode ?? 'default';
  const deptPart = JSON.stringify(overrides?.departmentIds ?? null);
  const lofPart = JSON.stringify(overrides?.lofIds ?? null);
  const fetchMode = options?.fetchMode ?? 'detail';
  const cacheKey = `${uid}\u001f${mode}\u001f${fetchMode}\u001f${reportingMonth}\u001f${category ?? ''}\u001f${deptPart}\u001f${lofPart}`;
  const existing = inflightMonthlyReporting.get(cacheKey);
  if (existing) return existing;
  const promise = fetchMonthlyReportingDataUncached(
    reportingMonth,
    category,
    roleScope,
    roleContext,
    overrides,
    options,
  )
    .then((result) =>
      finalizeMonthlyReportingResult(reportingMonth, category, overrides, options, result),
    )
    .finally(() => {
      inflightMonthlyReporting.delete(cacheKey);
    });
  inflightMonthlyReporting.set(cacheKey, promise);
  return promise;
};

export const getReportingResult = async ({
  reportingMonth,
  category,
  roleScope,
  roleContext,
  overrides,
  options,
}: GetReportingResultParams): Promise<ReportingResultSummary> => {
  const result = await fetchMonthlyReportingData(reportingMonth, category, roleScope, roleContext, overrides, options);
  return {
    totals: result.totals,
    averages: result.averages,
    submissionsCount: result.submissions.length,
    meta: {
      months: result.months,
    },
  };
};

const getTrimesterMonths = (reportingMonth: ReportingMonth): ReportingMonth[] =>
  getFiscalQuarterMonths(reportingMonth);

export const getYearMonths = (reportingMonth: ReportingMonth): ReportingMonth[] => {
  const monthDate = parseReportingMonth(reportingMonth);
  const months = Array.from({ length: 12 }, (_v, idx) => formatReportingMonth(monthDate.getFullYear(), idx));
  return months;
};

/**
 * Year view loads 12 months; Dashboard also loads yearly breakdown — up to 24 concurrent NN cloud calls.
 * For NN hospital-wide, use a small fixed pool so HTTP/callable requests complete without ~6+ minute serial waits.
 */
const shouldSerializeNnHospitalWideYearlyFetch = (
  category: string | undefined,
  roleContext: RoleContext | null | undefined,
  options?: ReportingFetchOptions,
): boolean => {
  if ((options?.mode ?? 'default') !== 'default') return false;
  if (!category?.trim()) return false;
  const normalized = resolveCategoryId(category);
  if (!normalized) return false;
  const canonical = resolveCanonicalCategoryId(normalized) ?? normalized;
  if (canonical !== 'nurse_notes') return false;
  return hasTeamCategoryHospitalWideAccess(roleContext, category);
};

type ReportingYearBatchRow = {
  reportingMonth: ReportingMonth;
  totals: Record<string, number>;
  averages: Record<string, number>;
  submissionsCount: number;
  message?: string;
  status?: 'ok' | 'error';
  error?: string;
};

/** Batched NN year callable when deployed; returns null to fall back to per-month pool. */
async function tryFetchReportingYearBatch(
  params: GetReportingResultParams,
  signal?: AbortSignal,
): Promise<ReportingYearBatchRow[] | null> {
  if (signal?.aborted) return null;
  if (!shouldSerializeNnHospitalWideYearlyFetch(params.category, params.roleContext, params.options)) {
    return null;
  }
  const authUser = getAuth(app).currentUser;
  if (!authUser) return null;

  const reportingMonths = getYearMonths(params.reportingMonth);
  const category = params.category?.trim() || 'nurse_notes';
  // Year-batch Cloud handler only accepts summary; never send detail (causes 400 on Hosting /api).
  const fetchMode = 'summary' as const;

  const tryYearBatchCallable = async (): Promise<ReportingYearBatchRow[] | null> => {
    try {
      const functions = getFunctions(app, 'us-central1');
      const fn = httpsCallable<
        { reportingMonths: ReportingMonth[]; category: string; fetchMode: string },
        { months: ReportingYearBatchRow[] }
      >(functions, 'fetchReportingYearBatch');
      const result = await fn({ reportingMonths, category, fetchMode });
      if (result.data?.months?.length) return result.data.months;
    } catch {
      return null;
    }
    return null;
  };

  try {
    const token = await authUser.getIdToken();
    const projectId = (app.options as { projectId?: string })?.projectId ?? '';
    const useSameOrigin = typeof window !== 'undefined' && !!window.location?.origin;
    const url =
      useSameOrigin && window.location?.origin
        ? `${window.location.origin}/api/fetchReportingYearBatch`
        : projectId
          ? `https://us-central1-${projectId}.cloudfunctions.net/api/fetchReportingYearBatch`
          : null;
    if (!url) return tryYearBatchCallable();

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), TEAM_REPORTING_CLOUD_TIMEOUT_MS * 2);
    if (signal) {
      signal.addEventListener('abort', () => controller.abort(), { once: true });
    }

    const res = await fetch(url, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ reportingMonths, category, fetchMode }),
      signal: controller.signal,
    });
    clearTimeout(timeout);
    if (!res.ok) {
      return tryYearBatchCallable();
    }
    const data = (await res.json()) as { months?: ReportingYearBatchRow[] };
    if (!data?.months || !Array.isArray(data.months)) return tryYearBatchCallable();
    return data.months;
  } catch {
    return tryYearBatchCallable();
  }
}

const fetchOneMonthForYear = (
  month: ReportingMonth,
  params: GetReportingResultParams,
): Promise<ReportingAggregateResult> =>
  fetchMonthlyReportingData(
    month,
    params.category,
    params.roleScope,
    params.roleContext,
    params.overrides,
    params.options,
  ).then((result) => applySummaryFetchMode(result, params.options?.fetchMode));

/** Re-fetch a single month (year grid retry button). */
export const retryReportingMonth = async (
  month: ReportingMonth,
  params: GetReportingResultParams,
): Promise<ReportingYearMonthEntry> => {
  const entries = await getReportingResultsForMonths([month], params);
  return entries[0]!;
};

export const getReportingResultsForMonths = async (
  months: ReportingMonth[],
  params: GetReportingResultParams,
): Promise<ReportingYearMonthEntry[]> => {
  // NN hospital-wide: reuse year-batch (same source as year view) then slice to requested months.
  // Per-month client/LOF paths can under-count vs the Admin SDK batch (e.g. trimester Q2).
  if (
    months.length > 0 &&
    shouldSerializeNnHospitalWideYearlyFetch(params.category, params.roleContext, params.options)
  ) {
    const batch = await tryFetchReportingYearBatch(params);
    if (batch?.length) {
      const byMonth = new Map(batch.map((row) => [row.reportingMonth, row] as const));
      if (months.every((month) => byMonth.has(month))) {
        return months.map((reportingMonth) => {
          const row = byMonth.get(reportingMonth)!;
          return {
            reportingMonth,
            result: applySummaryFetchMode(
              {
                submissions: [],
                totals: row.totals,
                averages: row.averages,
                months: [reportingMonth],
                message: row.message,
                submissionsCount: row.submissionsCount,
              },
              params.options?.fetchMode,
            ),
            status: (row.status === 'error' ? 'error' : 'ok') as 'ok' | 'error',
            error: row.error,
          };
        });
      }
    }
  }

  const serialize = shouldSerializeNnHospitalWideYearlyFetch(
    params.category,
    params.roleContext,
    params.options,
  );
  const poolSize = serialize ? NN_YEARLY_HOSPITAL_WIDE_POOL : DEFAULT_YEARLY_POOL_SIZE;

  const settled = await mapWithPool(months, poolSize, async (month) => {
    try {
      return { status: 'fulfilled' as const, value: await fetchOneMonthForYear(month, params) };
    } catch (reason) {
      return { status: 'rejected' as const, reason };
    }
  });

  return months.map((reportingMonth, index) => {
    const outcome = settled[index]!;
    if (outcome.status === 'fulfilled') {
      return { reportingMonth, result: outcome.value, status: 'ok' as const };
    }
    const message = outcome.reason instanceof Error ? outcome.reason.message : String(outcome.reason);
    return {
      reportingMonth,
      result: {
        submissions: [],
        totals: {},
        averages: {},
        months: [reportingMonth],
        message,
      },
      status: 'error' as const,
      error: message,
    };
  });
};

export type YearlyReportingProgressCallback = (
  entry: ReportingYearMonthEntry,
  loadedCount: number,
  totalCount: number,
) => void;

/** Progressive year load — invokes onMonth after each month completes (pool order). */
export const getYearlyReportingResultsProgressive = async (
  params: GetReportingResultParams,
  onMonth?: YearlyReportingProgressCallback,
  signal?: AbortSignal,
): Promise<ReportingYearMonthEntry[]> => {
  if (signal?.aborted) {
    throw new DOMException('Aborted', 'AbortError');
  }

  const batch = await tryFetchReportingYearBatch(params, signal);
  if (batch) {
    const entries = batch.map((row) => ({
      reportingMonth: row.reportingMonth,
      result: applySummaryFetchMode(
        {
          submissions: [],
          totals: row.totals,
          averages: row.averages,
          months: [row.reportingMonth],
          message: row.message,
          submissionsCount: row.submissionsCount,
        },
        params.options?.fetchMode,
      ),
      status: (row.status === 'error' ? 'error' : 'ok') as 'ok' | 'error',
      error: row.error,
    }));
    entries.forEach((entry, i) => onMonth?.(entry, i + 1, entries.length));
    return entries;
  }

  const months = getYearMonths(params.reportingMonth);
  const serialize = shouldSerializeNnHospitalWideYearlyFetch(
    params.category,
    params.roleContext,
    params.options,
  );
  const poolSize = serialize ? NN_YEARLY_HOSPITAL_WIDE_POOL : DEFAULT_YEARLY_POOL_SIZE;
  const entries: ReportingYearMonthEntry[] = new Array(months.length);
  let loaded = 0;

  await mapWithPool(months, poolSize, async (month, index) => {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    try {
      const result = await fetchOneMonthForYear(month, params);
      const entry: ReportingYearMonthEntry = { reportingMonth: month, result, status: 'ok' };
      entries[index] = entry;
    } catch (reason) {
      const message = reason instanceof Error ? reason.message : String(reason);
      entries[index] = {
        reportingMonth: month,
        result: { submissions: [], totals: {}, averages: {}, months: [month], message },
        status: 'error',
        error: message,
      };
    }
    loaded += 1;
    const entry = entries[index]!;
    onMonth?.(entry, loaded, months.length);
  });

  return entries;
};

export const getYearlyReportingResults = async (
  params: GetReportingResultParams,
): Promise<Array<{ reportingMonth: ReportingMonth; result: ReportingAggregateResult }>> => {
  const cacheKey = yearlyReportingInflightKey(params);
  let promise = inflightYearlyReporting.get(cacheKey);
  if (!promise) {
    promise = getYearlyReportingResultsProgressive(params)
      .then((entries) =>
        entries.map(({ reportingMonth, result }) => ({ reportingMonth, result })),
      )
      .finally(() => {
        inflightYearlyReporting.delete(cacheKey);
      });
    inflightYearlyReporting.set(cacheKey, promise);
  }

  return promise;
};

export const fetchTrimesterReportingData = async (
  reportingMonth: ReportingMonth,
  category?: string,
  roleScope?: RoleScope | null,
  roleContext?: RoleContext | null,
  overrides?: ReportingScopeOverrides,
  options?: ReportingFetchOptions,
): Promise<ReportingAggregateResult> => {
  const months = getTrimesterMonths(reportingMonth);
  const results = await mapWithPool(months, Math.min(DEFAULT_YEARLY_POOL_SIZE, months.length), (month) =>
    fetchMonthlyReportingData(month, category, roleScope, roleContext, overrides, options),
  );

  const submissions = results.flatMap((result) => result.submissions);
  const { totals, averages } = computeAggregates(submissions);

  return { submissions, totals, averages, months };
};

export const fetchYearReportingData = async (
  reportingMonth: ReportingMonth,
  category?: string,
  roleScope?: RoleScope | null,
  roleContext?: RoleContext | null,
  overrides?: ReportingScopeOverrides,
  options?: ReportingFetchOptions,
): Promise<ReportingAggregateResult> => {
  const months = getYearMonths(reportingMonth);
  const serialize = shouldSerializeNnHospitalWideYearlyFetch(category, roleContext, options);

  const poolSize = serialize ? NN_YEARLY_HOSPITAL_WIDE_POOL : DEFAULT_YEARLY_POOL_SIZE;
  const results = await mapWithPool(months, poolSize, (month) =>
    fetchMonthlyReportingData(month, category, roleScope, roleContext, overrides, options).then((r) =>
      applySummaryFetchMode(r, options?.fetchMode),
    ),
  );

  const submissions = results.flatMap((result) => result.submissions);
  const { totals, averages } = computeAggregates(submissions);

  return { submissions, totals, averages, months };
};

export const fetchReportingData = async (
  view: 'monthly' | 'trimester' | 'year',
  reportingMonth: ReportingMonth,
  category?: string,
  roleScope?: RoleScope | null,
  roleContext?: RoleContext | null,
  overrides?: ReportingScopeOverrides,
  options?: ReportingFetchOptions,
): Promise<ReportingAggregateResult> => {
  if (view === 'trimester') {
    return fetchTrimesterReportingData(reportingMonth, category, roleScope, roleContext, overrides, options);
  }

  if (view === 'year') {
    return fetchYearReportingData(reportingMonth, category, roleScope, roleContext, overrides, options);
  }

  return fetchMonthlyReportingData(reportingMonth, category, roleScope, roleContext, overrides, options);
};
